import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
  Alert, Button, Drawer, Form, Input, InputNumber, Select, Segmented,
  Modal, Space, Switch, Table, Tag, Tooltip, Upload, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, CloudUploadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined,
  PlayCircleOutlined, QuestionCircleOutlined, ReloadOutlined, SettingOutlined,
} from '@ant-design/icons';
import { useProjectStore } from '../../hooks/useProjectStore';
import { useAuthStore } from '../../hooks/useAuthStore';
import {
  DataHubApplication, DataHubAuditIssue, DataHubAuditRow, DataHubBusinessInventoryModule,
  DataHubColumn, DataHubCurvePoint, DataHubCurveResponse,
  DataHubDataset, DataHubProcessOptions, DataHubReadiness, DataHubSource,
  DataHubType, RealWellAcceptance, RealWellStageAcceptance, datahubAPI,
} from '../../services/api';
import { GEOMECHANICS_UI_ENABLED } from '../geomechanics/feature';
import OverviewModule from '../geomechanics/components/OverviewModule';
import './DataHubPage.css';

const DATA_TYPES: Array<{ value: DataHubType; label: string; downstream?: string }> = [
  { value: 'well_log', label: '测井数据', downstream: '测井解释、岩石力学' },
  { value: 'survey', label: '井轨迹', downstream: '轨迹计算、深度换算' },
  { value: 'formation', label: '地层分层', downstream: '地层剖面、层位约束' },
  { value: 'pressure', label: '压力数据', downstream: '三压力、井壁稳定' },
  { value: 'mechanics', label: '岩石力学', downstream: '力学剖面、稳定性评价' },
  { value: 'mud_log', label: '录井数据', downstream: '岩性解释、综合剖面' },
  { value: 'lab_test', label: '岩心实验', downstream: '力学参数标定' },
  { value: 'pressure_test', label: '压力测试', downstream: '孔压与破裂压力约束' },
  { value: 'wellbore_event', label: '井壁事件', downstream: '坍塌校核、风险复盘' },
  { value: 'mud_program', label: '泥浆程序', downstream: '安全密度窗口' },
  { value: 'fracture', label: '压裂施工', downstream: '施工分析、压裂评价' },
  { value: 'microseismic', label: '微地震', downstream: '裂缝展布评价' },
  { value: 'seismic', label: '地震解释', downstream: '构造与属性分析' },
  { value: 'other', label: '其他数据', downstream: '项目资料' },
];

const WORKBENCH_INPUT_TARGETS: Partial<Record<DataHubType, { label: string; route: string }>> = {
  lab_test: { label: '进入 04 岩石力学使用', route: '/mechanics/workbench/04' },
  pressure_test: { label: '进入 06 破裂压力使用', route: '/mechanics/workbench/06' },
  wellbore_event: { label: '进入 07 坍塌压力使用', route: '/mechanics/workbench/07' },
  mud_program: { label: '进入 08 综合评价使用', route: '/mechanics/workbench/08' },
};
const isWorkbenchInputType = (value?: DataHubType) => !!value && Boolean(WORKBENCH_INPUT_TARGETS[value]);

// 只有连续曲线/点序列类型才适用“曲线异常校正”；
// 离散测试、区间、事件类数据按行核对，不合成单条曲线。
const CURVE_PREVIEW_TYPES = new Set<DataHubType>([
  'well_log', 'survey', 'pressure', 'mechanics', 'mud_log',
  'fracture', 'seismic',
]);
const isCurvePreviewType = (value?: DataHubType) => !!value && CURVE_PREVIEW_TYPES.has(value);

const FIELD_OPTIONS: Record<DataHubType, Array<{ value: string; label: string; unit?: string; type?: DataHubColumn['value_type'] }>> = {
  well_log: [
    ['depth', 'DEPTH 测深/深度', 'm'], ['gr', 'GR 自然伽马', 'API'], ['sp', 'SP 自然电位', 'mV'],
    ['tvd', 'TVD 垂深', 'm'],
    ['cali', 'CAL 井径', 'mm'], ['bit_size', 'BS 钻头尺寸', 'mm'],
    ['rt', 'RT 地层真电阻率/深探测电阻率', 'ohm.m'], ['rd', 'RD/ILD 深侧向/深感应电阻率', 'ohm.m'],
    ['ri', 'RI 中探测电阻率', 'ohm.m'], ['im', 'IM/ILM 中感应电阻率', 'ohm.m'],
    ['rs', 'RS/LLS 浅探测电阻率', 'ohm.m'], ['ils', 'ILS/SFL 浅感应电阻率', 'ohm.m'],
    ['msfl', 'MSFL 微球形聚焦电阻率', 'ohm.m'], ['rxo', 'RXO 冲洗带电阻率', 'ohm.m'], ['rw', 'RW 地层水电阻率', 'ohm.m'],
    ['den', 'DEN/RHOB 岩性密度/体积密度', 'g/cm3'], ['cnl', 'CNL/NPHI 补偿中子孔隙度', '%'],
    ['ac', 'AC/DT 声波时差', 'us/m'], ['dts', 'DTS 横波时差', 'us/m'], ['pe', 'PE/PEF 光电吸收截面指数', 'barn/e'],
    ['porosity', 'PHI/POR 解释孔隙度', '%'], ['permeability', 'PERM 解释渗透率', 'mD'],
    ['water_saturation', 'SW 含水饱和度', '%'], ['shale_volume', 'VSH 泥质含量', '%'],
    ['toc', 'TOC 总有机碳', '%'], ['brittleness', 'BI 脆性指数', '%'],
    ['young_modulus', '杨氏模量', 'GPa'], ['poisson_ratio', '泊松比', ''], ['min_horizontal_stress', '最小水平主应力', 'MPa'],
    ['p_wave_velocity', '纵波速度', 'm/s'], ['s_wave_velocity', '横波速度', 'm/s'],
    ['normal_sonic', '正常压实声波时差', 'us/ft'], ['biot_coefficient', 'Biot系数', ''],
    ['inclination', 'DEV/INC 井斜角', 'deg'], ['azimuth', 'AZI 方位角', 'deg'],
    ['nmr_t2', 'NMR/T2 核磁 T2', 'ms'], ['fmi', 'FMI/UBI 成像测井', ''],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  survey: [
    ['md', 'MD 测深', 'm'], ['inclination', '井斜角', 'deg'], ['azimuth', '方位角', 'deg'],
    ['tvd', 'TVD 垂深', 'm'], ['ns', '南北位移', 'm'], ['ew', '东西位移', 'm'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  formation: [
    { value: 'name', label: '层名', type: 'text' }, { value: 'top_depth', label: '顶深', unit: 'm' },
    { value: 'bottom_depth', label: '底深', unit: 'm' }, { value: 'lithology', label: '岩性', type: 'text' },
    { value: 'is_target', label: '目标层', type: 'boolean' },
  ],
  pressure: [
    ['depth', '深度', 'm'], ['pore_pressure', '孔隙压力', 'MPa'], ['collapse_pressure', '坍塌压力', 'MPa'],
    ['fracture_pressure', '破裂压力', 'MPa'], ['mud_weight', '泥浆密度', 'g/cm3'],
    ['vertical_stress', '上覆压力', 'MPa'], ['vertical_stress_gradient', '上覆压力梯度', 'MPa/m'],
    ['pore_pressure_gradient', '孔隙压力梯度', 'MPa/m'], ['collapse_pressure_gradient', '坍塌压力梯度', 'MPa/100m'],
    ['fracture_pressure_gradient', '破裂压力梯度', 'MPa/100m'], ['pore_pressure_emw', '孔隙压力当量密度', 'g/cm3'],
    ['collapse_pressure_emw', '坍塌压力当量密度', 'g/cm3'], ['fracture_pressure_emw', '破裂压力当量密度', 'g/cm3'],
    ['overburden_pressure', '上覆岩层压力', 'MPa'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  mechanics: [
    ['depth', '深度', 'm'], ['youngs_modulus', '杨氏模量', 'GPa'], ['poissons_ratio', '泊松比', ''],
    ['dynamic_youngs_modulus', '动态杨氏模量', 'GPa'], ['static_youngs_modulus', '静态杨氏模量', 'GPa'],
    ['dynamic_poissons_ratio', '动态泊松比', ''], ['static_poissons_ratio', '静态泊松比', ''],
    ['ucs', 'UCS', 'MPa'], ['tensile_strength', '抗拉强度', 'MPa'], ['friction_angle', '内摩擦角', 'deg'],
    ['cohesion', '内聚力', 'MPa'], ['brittleness_index', '脆性指数', ''], ['sigma_v', '垂向应力', 'MPa'],
    ['sigma_h', '最小水平主应力', 'MPa'], ['sigma_h_max', '最大水平主应力', 'MPa'], ['stress_direction', '最大主应力方向', 'deg'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  lab_test: [
    ['depth', '深度', 'm'], ['sample_id', '样本编号', ''], ['layer_id', '层号', ''],
    ['E_sta_lab', '实验杨氏模量', 'GPa'], ['nu_sta_lab', '实验泊松比', ''],
    ['UCS_lab', '实验抗压强度', 'MPa'], ['phi_lab', '实验内摩擦角', 'deg'],
    ['C_lab', '实验内聚力', 'MPa'], ['T0_lab', '实验抗拉强度', 'MPa'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  pressure_test: [
    ['depth', '深度', 'm'], ['tvd', 'TVD 垂深', 'm'], ['test_type', '测试类型', ''],
    ['value_MPa', '测试值', 'MPa'], ['unit', '单位', ''],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  wellbore_event: [
    ['depth', '深度', 'm'], ['event_type', '事件类型', ''], ['severity', '严重程度', ''],
    ['note', '备注', ''],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  mud_program: [
    ['top_depth', '顶深', 'm'], ['bottom_depth', '底深', 'm'],
    ['density_min', '最小密度', 'g/cm3'], ['density_max', '最大密度', 'g/cm3'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  fracture: [
    ['timestamp', 'Absolute / clock time', ''], ['elapsed_time_s', 'Elapsed time', 's'],
    ['wellhead_pressure_mpa', 'WHP / 井口压力', 'MPa'], ['pump_rate_m3min', 'Pump rate / 泵注排量', 'm3/min'],
    ['slurry_rate_m3min', 'Slurry rate / 混浆排量', 'm3/min'], ['fluid_rate_m3min', 'Fluid rate / 净液排量', 'm3/min'],
    ['proppant_concentration_kgm3', 'Proppant concentration / 砂浓度', 'kg/m3'],
    ['proppant_rate_kgmin', 'Proppant rate', 'kg/min'], ['proppant_mass_kg', 'Proppant mass', 'kg'],
    ['pumping_stage', 'Pumping stage', ''],
    ['time_s', '时间', 's'], ['stage_number', '段号', ''], ['surface_pressure_mpa', '井口/施工压力', 'MPa'],
    ['bottomhole_pressure_mpa', '实测井底压力', 'MPa'], ['rate_m3min', '排量', 'm3/min'],
    ['fluid_density_kgm3', '液体密度', 'kg/m3'], ['wellbore_friction_mpa', '井筒摩阻', 'MPa'],
    ['perforation_loss_mpa', '声明的射孔压降', 'MPa'], ['closure_pressure_mpa', 'DFIT闭合压力', 'MPa'],
    ['interpreted_shmin_mpa', '解释Shmin', 'MPa'], ['isip_mpa', 'ISIP', 'MPa'],
    ['breakdown_pressure_mpa', '破裂压力', 'MPa'], ['interpreted_leakoff', '解释滤失', ''],
    ['proppant_conc_kgm3', '砂比', 'kg/m3'], ['cum_fluid_m3', '累计液量', 'm3'], ['cum_proppant_t', '累计砂量', 't'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  microseismic: [
    ['stage_number', '段号', ''], ['x', 'X 坐标', 'm'], ['y', 'Y 坐标', 'm'], ['z', 'Z/TVD', 'm'], ['event_time', '事件时间', ''],
    ['magnitude', '震级', ''], ['energy', '能量', ''],
    ['interpreted_height_m', '解释裂缝高度', 'm'], ['interpreted_half_length_m', '解释半缝长', 'm'],
    ['interpreted_total_length_m', '解释总缝长', 'm'], ['interpreted_srv_m3', '解释SRV', 'm3'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  seismic: [
    ['x', 'X 坐标', 'm'], ['y', 'Y 坐标', 'm'], ['depth', '深度/TVD', 'm'], ['attribute', '属性值', ''],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  mud_log: [
    ['depth', '深度', 'm'], ['lithology', '岩性', ''], ['gas_total', '全烃', '%'], ['rop', '钻时/机械钻速', 'm/h'],
  ].map(([value, label, unit]) => ({ value, label, unit })),
  other: [],
};

type DataTypeExample = {
  formats: string[];
  note: string;
  columns: Array<{ value: string; label: string; unit?: string; type?: DataHubColumn['value_type'] }>;
  sampleRows: Array<Record<string, string | number | boolean>>;
};

const EXAMPLE_VALUES: Record<string, string | number | boolean> = {
  depth: 2738, md: 2738, tvd: 2700, top_depth: 2738, bottom_depth: 2750,
  gr: 71.1, rt: 6.6, rd: 6.6, den: 2.591, ac: 188.7, dts: 108.2,
  cal: 8.7, cnl: 23.1, pe: 6.0, porosity: 5.2, permeability: 0.12,
  water_saturation: 38.5, shale_volume: 35.0, toc: 2.4, brittleness: 0.40,
  young_modulus: 39.7, poisson_ratio: 0.24, min_horizontal_stress: 81.3,
  p_wave_velocity: 5298, s_wave_velocity: 3120, normal_sonic: 188.7,
  biot_coefficient: 1, inclination: 88.5, azimuth: 120, ns: 1200, ew: -800,
  dls: 2.1, name: '龙马溪水平段', lithology: '页岩', is_target: true,
  top_TVD: 3549.83, bottom_TVD: 3624.73, sample_id: 'LAB-01', layer_id: 'L1',
  E_sta_lab: 40.2, nu_sta_lab: 0.25, UCS_lab: 65.0, phi_lab: 32,
  C_lab: 14.0, T0_lab: 5.2, test_type: 'pore_pressure', value_MPa: 55.3,
  unit: 'MPa', event_type: 'breakout', severity: 3, note: '扩径',
  density_min: 1.35, density_max: 1.55, time_s: 0, surface_pressure_mpa: 12.5,
  rate_m3min: 4.2, proppant_conc_kgm3: 180, cum_fluid_m3: 12.3, cum_proppant_t: 0.9,
  x: 100, y: 200, z: 3000, event_time: '2024-01-01 12:00:00', magnitude: 0.8,
  energy: 1200000, gas_total: 0.85, rop: 4.5, stress_direction: 120,
  sigma_v: 90.4, sigma_h: 81.3, sigma_h_max: 92.3, rw: 0.01, il: 10,
  bit_size: 215.9, ils: 8.5, rs: 7.2, msfl: 7.0, rxo: 6.8, ri: 7.8,
  im: 8.0, nmr_t2: 45, fmi: '成像',
};

function buildExampleRows(
  columns: DataTypeExample['columns'],
  count = 2,
): Array<Record<string, string | number | boolean>> {
  return Array.from({ length: count }, (_, index) => {
    const row: Record<string, string | number | boolean> = {};
    columns.forEach((column) => {
      const value = EXAMPLE_VALUES[column.value];
      if (typeof value === 'number') {
        row[column.value] = Number((value * (index === 0 ? 1 : 1.02)).toFixed(3));
      } else {
        row[column.value] = value ?? (column.type === 'text' || !column.unit ? '示例' : 1);
      }
    });
    return row;
  });
}

const DATA_TYPE_EXAMPLES: Record<DataHubType, DataTypeExample> = {
  well_log: {
    formats: ['LAS', 'CSV', 'TXT', 'XLSX'],
    note: '每列是一条测井曲线，深度使用测量深度 MD（m）。请至少把 DEN、AC、GR、RT 中的一列对应到系统字段；声波时差支持 us/ft 或 us/m，读取后请确认单位。',
    columns: FIELD_OPTIONS.well_log,
    sampleRows: buildExampleRows(FIELD_OPTIONS.well_log),
  },
  mud_log: {
    formats: ['CSV', 'XLSX'],
    note: '录井按深度或时间记录，至少包含一列深度或时间，以及一项录井参数（全烃、钻时等）。',
    columns: FIELD_OPTIONS.mud_log,
    sampleRows: buildExampleRows(FIELD_OPTIONS.mud_log),
  },
  survey: {
    formats: ['CSV', 'XLSX'],
    note: '井眼轨迹测点表：MD/INC/AZI/TVD/NS/EW 为推荐标准列，用于最小曲率轨迹计算和 MD-TVD 换算。',
    columns: FIELD_OPTIONS.survey,
    sampleRows: buildExampleRows(FIELD_OPTIONS.survey),
  },
  formation: {
    formats: ['CSV', 'XLSX'],
    note: '区间表：每行一个层段，name/top_depth/bottom_depth/lithology/is_target。',
    columns: FIELD_OPTIONS.formation,
    sampleRows: buildExampleRows(FIELD_OPTIONS.formation),
  },
  pressure: {
    formats: ['CSV', 'XLSX'],
    note: '压力剖面逐点表：深度 + 孔隙/坍塌/破裂压力或梯度/当量密度，深度基准建议 TVD。',
    columns: FIELD_OPTIONS.pressure,
    sampleRows: buildExampleRows(FIELD_OPTIONS.pressure),
  },
  mechanics: {
    formats: ['CSV', 'XLSX'],
    note: '岩石力学逐点表：包含深度和弹性、强度或应力数据，请至少把一列对应到系统字段。',
    columns: FIELD_OPTIONS.mechanics,
    sampleRows: buildExampleRows(FIELD_OPTIONS.mechanics),
  },
  lab_test: {
    formats: ['CSV', 'XLSX'],
    note: '岩心实验样本：sample_id + 深度 + 六参数（E/nu/UCS/phi/C/T0），供 04 实验标定使用。',
    columns: FIELD_OPTIONS.lab_test,
    sampleRows: buildExampleRows(FIELD_OPTIONS.lab_test),
  },
  pressure_test: {
    formats: ['CSV', 'XLSX'],
    note: '孔压/LOT/XLOT/FIT/漏失测试：test_type + 深度 + 值（MPa），供压力约束和井壁稳定校核。',
    columns: FIELD_OPTIONS.pressure_test,
    sampleRows: buildExampleRows(FIELD_OPTIONS.pressure_test),
  },
  wellbore_event: {
    formats: ['CSV', 'XLSX'],
    note: '井壁事件表：深度 + 事件类型（breakout/cavings/tight/stuck 等）+ 严重程度 + 备注。',
    columns: FIELD_OPTIONS.wellbore_event,
    sampleRows: buildExampleRows(FIELD_OPTIONS.wellbore_event),
  },
  mud_program: {
    formats: ['CSV', 'XLSX'],
    note: '泥浆程序区间表：top_depth/bottom_depth + 密度上下限（g/cm³），供安全窗口约束使用。',
    columns: FIELD_OPTIONS.mud_program,
    sampleRows: buildExampleRows(FIELD_OPTIONS.mud_program),
  },
  fracture: {
    formats: ['CSV', 'XLSX'],
    note: '压裂施工时间序列：时间 + 压力/排量/砂比/累计液量/累计砂量。',
    columns: FIELD_OPTIONS.fracture,
    sampleRows: buildExampleRows(FIELD_OPTIONS.fracture),
  },
  microseismic: {
    formats: ['XLSX', 'CSV'],
    note: '微地震事件表：x/y/z（或 TVD）+ 事件时间 + 震级/能量。',
    columns: FIELD_OPTIONS.microseismic,
    sampleRows: buildExampleRows(FIELD_OPTIONS.microseismic),
  },
  seismic: {
    formats: ['CSV', 'XLSX'],
    note: '地震解释属性表：x/y/depth + 属性值。',
    columns: FIELD_OPTIONS.seismic,
    sampleRows: buildExampleRows(FIELD_OPTIONS.seismic),
  },
  other: {
    formats: ['CSV', 'XLSX'],
    note: '其它表格数据：上传后请配置标准字段和单位。',
    columns: FIELD_OPTIONS.other,
    sampleRows: buildExampleRows(FIELD_OPTIONS.other),
  },
};

function downloadTypeTemplate(dataType: DataHubType, example: DataTypeExample) {
  const headers = example.columns.map((column) => column.value);
  const lines = [headers.join(',')];
  lines.push(example.columns.map((column) => column.unit ?? '').join(','));
  example.sampleRows.forEach((row) => {
    lines.push(headers.map((header) => String(row[header] ?? '')).join(','));
  });
  const blob = new Blob(['\ufeff', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `datahub-template-${dataType}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  uploaded: { label: '已上传', color: 'default' },
  parsed: { label: '已读取', color: 'processing' },
  processed: { label: '已处理', color: 'success' },
  failed: { label: '失败', color: 'error' },
  ready: { label: '可用', color: 'success' },
  published: { label: '当前版本', color: 'blue' },
};

const dataTypeLabel = (value?: DataHubType) => DATA_TYPES.find((item) => item.value === value)?.label || value || '-';
const defaultDepthReference = (value?: DataHubType): 'MD' | 'TVD' | 'TVDSS' | '' => {
  if (
    value === 'well_log'
    || value === 'survey'
    || value === 'mechanics'
    || value === 'lab_test'
    || value === 'pressure_test'
    || value === 'wellbore_event'
  ) return 'MD';
  if (value === 'pressure') return 'TVD';
  return '';
};

const SOURCE_UNITS_BY_TARGET: Record<string, string[]> = {
  m: ['m', 'ft', 'cm', 'mm'],
  mm: ['mm', 'cm', 'in', 'm'],
  'g/cm3': ['g/cm3', 'kg/m3', 'lb/ft3'],
  'us/m': ['us/m', 'us/ft'],
  'us/ft': ['us/ft', 'us/m'],
  'ohm.m': ['ohm.m', 'ohm.ft'],
  MPa: ['MPa', 'kPa', 'Pa', 'bar', 'psi'],
  GPa: ['GPa', 'MPa', 'psi'],
  '%': ['%', 'fraction'],
  'm/s': ['m/s', 'ft/s'],
  deg: ['deg', 'rad'],
  s: ['s', 'ms', 'min', 'h'],
  'm3/min': ['m3/min', 'm3/h', 'L/s', 'bpm', 'bbl/min'],
  'kg/m3': ['kg/m3', 'g/L'],
};

function unitOptions(column: DataHubColumn, targetUnit: string) {
  return Array.from(new Set([
    ...(SOURCE_UNITS_BY_TARGET[targetUnit] || []),
    ...(column.unit_options || []),
    column.unit || '',
    targetUnit,
  ].filter(Boolean))).map((value) => ({ value, label: value }));
}

const CURVE_PRIORITY = [
  'gr', 'rt', 'rd', 'rxo', 'den', 'ac', 'dts', 'cnl', 'porosity',
  'water_saturation', 'permeability', 'cali', 'sp', 'toc', 'brittleness',
];
const NON_REVIEW_CURVES = new Set([
  'depth', 'md', 'tvd', 'time_s', 'x', 'y', 'z', 'ns', 'ew', 'inclination', 'azimuth',
  // Formation interval boundaries are table fields, not sampled curves, and
  // formation datasets do not have a depth/time axis for curve rendering.
  'top_depth', 'bottom_depth',
]);

function reviewableCurveColumns(columns: DataHubColumn[]) {
  return columns.filter((column) => {
    const semanticField = column.standard_field || column.dataset_field || '';
    return column.value_type === 'number' && Boolean(column.dataset_field) && !NON_REVIEW_CURVES.has(semanticField);
  });
}

function defaultCurveField(columns: DataHubColumn[]) {
  const candidates = reviewableCurveColumns(columns);
  return [...candidates].sort((left, right) => {
    const leftRank = CURVE_PRIORITY.indexOf(left.standard_field || left.dataset_field || '');
    const rightRank = CURVE_PRIORITY.indexOf(right.standard_field || right.dataset_field || '');
    return (leftRank < 0 ? 999 : leftRank) - (rightRank < 0 ? 999 : rightRank);
  })[0]?.dataset_field || '';
}

type AuditCellRef = { row_index: number; field: string };
type AuditReason = {
  id: number;
  key: string;
  label: string;
  issue_type: string;
  action: string;
  message: string;
  severity: string;
};

const REASON_MARKS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫', '⑬', '⑭', '⑮'];
const REASON_ORDER = [
  'null_marker', 'non_numeric', 'range', 'iqr', 'zscore', 'hampel',
  'unit_conversion', 'smoothing', 'resample', 'manual_edit', 'user_exclude', '__derived_difference__',
];

const ISSUE_TYPE_LABELS: Record<string, string> = {
  null_marker: '缺失值标记',
  non_numeric: '非数值内容',
  range_low: '低于物理下限',
  range_high: '高于物理上限',
  range: '超出物理值域',
  iqr_low: '整体分布中的异常低值',
  iqr_high: '整体分布中的异常高值',
  iqr: '整体分布异常值',
  zscore_low: '明显低于平均值',
  zscore_high: '明显高于平均值',
  zscore: '明显偏离平均值',
  hampel_low: '局部突降值',
  hampel_high: '局部突升值',
  hampel: '局部突变值',
  axis_missing: '深度或时间缺失',
  axis_duplicate: '深度或时间重复',
  axis_non_monotonic: '深度或时间顺序折返',
  axis_large_gap: '深度或时间可能缺少一段',
  unit_conversion: '单位换算',
  smoothing: '平滑处理',
  resample: '重采样',
  derived: '处理差异',
  manual_edit: '人工修改',
  user_exclude: '人工剔除',
};

const ACTION_LABELS: Record<string, string> = {
  null: '置空',
  clip: '边界截断',
  keep: '保留原值并标记',
  cleaned: '系统已处理',
  converted: '单位换算',
  smoothed: '平滑',
  resampled: '重采样',
  modified: '人工修改',
  excluded: '排除点位',
};

const issueTypeLabel = (value?: string) => ISSUE_TYPE_LABELS[value || ''] || value || '-';
const actionLabel = (value?: string) => ACTION_LABELS[value || ''] || value || '-';
const PRESSURE_LOCATION_LABELS: Record<string, string> = {
  UNKNOWN: '暂不确定', WELLHEAD: '井口', BOTTOMHOLE: '井底', PRE_PERFORATION: '射孔前', FRACTURE_ENTRY: '裂缝入口',
};
const PRESSURE_ROLE_LABELS: Record<string, string> = {
  WELLHEAD: '井口压力', BOTTOMHOLE: '井底压力', CASING: '套管压力', ANNULUS: '环空压力', PUMP: '泵压', UNKNOWN: '用途待确认',
};
const RATE_ROLE_LABELS: Record<string, string> = {
  TOTAL_SLURRY: '总排量', CARRIER_FLUID: '携砂液排量', PUMP_RATE: '泵注排量', UNKNOWN: '用途待确认',
};
const CONFIDENCE_LABELS: Record<string, string> = {
  High: '把握较高', Medium: '把握一般', Low: '需要确认', HIGH: '把握较高', MEDIUM: '把握一般', LOW: '需要确认',
  'User Confirmed': '已人工确认', Unknown: '暂不确定', UNKNOWN: '暂不确定',
};
const readableConfidence = (value?: string | null) => CONFIDENCE_LABELS[value || ''] || value || '-';
const SYSTEM_VALUE_LABELS: Record<string, string> = {
  AUTO_TEMPLATE: '系统按文件模板自动对应', AUTO_GENERIC: '系统按通用规则自动对应', MANUAL: '人工指定', AUTO_LIKELIHOOD: '系统自动选择',
  'FIELD FILE': '现场文件', ELAPSED: '累计时间', ABSOLUTE: '日期时间', UNRESOLVED: '尚未识别',
  GENERIC_FRACTURING: '通用压裂格式', 'Generic Fracturing': '通用压裂格式',
  VENDOR_LIKE_ENGLISH_CSV: '英文 CSV 常用格式', VENDOR_LIKE_CHINESE_WORKBOOK: '中文工作簿常用格式',
};
const readableSystemValue = (value?: string | null) => SYSTEM_VALUE_LABELS[value || ''] || value || '-';
const readableChannelRole = (value: string) => PRESSURE_ROLE_LABELS[value] || RATE_ROLE_LABELS[value] || readableSystemValue(value);
const DIAGNOSTIC_LABELS: Record<string, string> = {
  FIELD_FRICTION_NOT_INTERPRETED: '尚未完成现场摩阻分析', FIELD_FRICTION_INVALID: '现场摩阻结果无效',
  FIELD_FRICTION_NON_MONOTONIC: '现场摩阻与排量的变化关系需要确认', BHP_FIELD_VALIDATION_UNAVAILABLE: '没有实测井底压力可验证',
  STEP_INTERPRETATION_NOT_AVAILABLE: '尚未完成台阶识别', TIME_DUPLICATE: '存在重复时间', TIME_UNSORTED: '时间顺序需要确认',
  TIME_GAP: '时间间隔过大', STAGE_BOUNDARY_AMBIGUOUS: '段号边界需要确认', STAGE_UNRESOLVED: '未识别段号',
  PRESSURE_NEGATIVE: '存在负压力值', RATE_NEGATIVE: '存在负排量值', VENDOR_TEMPLATE_LOW_CONFIDENCE: '文件格式模板需要确认',
  ALIAS_AMBIGUOUS: '有字段存在多个可能用途',
};
const readableDiagnostic = (value: string) => DIAGNOSTIC_LABELS[value] || value;
const readableBackendText = (value?: string | null) => String(value || '')
  .replace(/Published Dataset/gi, '应用版本')
  .replace(/DataHub\s*曲线/gi, '测井曲线')
  .replace(/DataHub/g, '数据中心')
  .replace(/当前有效版本/g, '当前版本')
  .replace(/字段映射/g, '字段配置')
  .replace(/上游/g, '数据来源')
  .replace(/下游契约/g, '关联模块校验')
  .replace(/下游/g, '关联模块')
  .replace(/质控/g, '质量校验')
  .replace(/版本生效/g, '版本应用')
  .replace(/生效/g, '应用')
  .replace(/契约/g, '校验规则')
  .replace(/置信度/g, '匹配度');
const DECISION_LABELS: Record<NonNullable<DataHubCurvePoint['decision']>, string> = {
  accepted: '已确认保留',
  raw: '已恢复原始观测',
  modified: '已人工修改',
  excluded: '已剔除',
};
const decisionLabel = (value?: DataHubCurvePoint['decision']) => value ? DECISION_LABELS[value] : '待校正';
type CurveDecision = 'accept' | 'raw' | 'edit' | 'exclude';
type PendingCurveDecision = {
  row_index: number;
  field: string;
  decision: CurveDecision;
  value?: number | null;
};

const pendingCurveDecisionKey = (field: string, rowIndex: number) => `${field}:${rowIndex}`;
const MAPPING_TEMPLATE_KEY = 'fracsight:datahub:fracturing-test-mapping-template:v1';

function toMapping(
  source: DataHubSource,
  overrides: Record<string, Partial<DataHubColumn>>,
  excludedSourceKeys: Set<string>,
): DataHubProcessOptions['mapping'] {
  const used = new Set<string>();
  return source.columns.filter((column) => !excludedSourceKeys.has(column.source_key)).map((column) => {
    const patch = overrides[column.source_key] || {};
    const hasStandardOverride = Object.prototype.hasOwnProperty.call(patch, 'standard_field');
    const selected = hasStandardOverride ? patch.standard_field : column.standard_field;
    const baseField = selected || (column.value_type === 'number' ? `custom_${column.source_key.replace(/[^\w\u4e00-\u9fa5]+/g, '_').toLowerCase()}` : '');
    if (!baseField) return null;
    let datasetField = String(patch.dataset_field || baseField);
    if (used.has(datasetField)) {
      let index = 2;
      while (used.has(`${datasetField}_${index}`)) index += 1;
      datasetField = `${datasetField}_${index}`;
    }
    used.add(datasetField);
    return {
      source_key: column.source_key,
      dataset_field: datasetField,
      standard_field: selected || null,
      display_name: column.display_name,
      source_unit: patch.source_unit ?? column.unit ?? '',
      target_unit: patch.target_unit ?? column.target_unit ?? '',
      // The backend derives and validates the conversion from source_unit to
      // the fixed standard target unit.  This value is display-only.
      conversion: 'auto',
      value_type: patch.value_type ?? column.value_type ?? 'number',
      confirmation_required: patch.confirmation_required ?? column.confirmation_required ?? false,
    };
  }).filter(Boolean) as DataHubProcessOptions['mapping'];
}

function compactNumber(value: unknown) {
  if (typeof value !== 'number') return value ?? '-';
  if (!Number.isFinite(value)) return '-';
  const abs = Math.abs(value);
  if ((abs > 0 && abs < 0.001) || abs >= 1_000_000) return value.toExponential(4);
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (Number.isInteger(value)) return value;
  return Number(value.toPrecision(abs >= 10 ? 6 : 7)).toString();
}

function displayValue(value: unknown) {
  return String(compactNumber(value));
}

function exactValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toPrecision(12).replace(/\.?0+$/, '');
  return value === null || value === undefined || value === '' ? '空' : String(value);
}

function escapeHtml(value: unknown) {
  return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[character] || character));
}

function comparableValue(value: unknown) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(Number(value.toFixed(6))) : '';
  const numeric = Number(String(value).replace(/,/g, '').trim());
  if (Number.isFinite(numeric) && String(value).trim() !== '') return String(Number(numeric.toFixed(6)));
  return String(value).trim();
}

function valuesEqual(left: unknown, right: unknown) {
  return comparableValue(left) === comparableValue(right);
}

function cellKey(rowIndex: number, field: string) {
  return `${rowIndex}:${field}`;
}

function parseCellKey(key: string): AuditCellRef | null {
  const [row, ...fieldParts] = key.split(':');
  const rowIndex = Number(row);
  const field = fieldParts.join(':');
  if (!Number.isInteger(rowIndex) || !field) return null;
  return { row_index: rowIndex, field };
}

function apiErrorMessage(error: any, fallback: string) {
  const status = error?.response?.status;
  const detail = error?.response?.data;
  if (error?.code === 'ECONNABORTED') {
    return `${fallback}。文件较大，系统可能仍在继续处理，请稍后刷新原始文件列表查看结果。`;
  }
  if (typeof detail === 'string') {
    const lowerDetail = detail.toLowerCase();
    const looksLikeHtml = lowerDetail.includes('<!doctype html')
      || lowerDetail.includes('<html')
      || lowerDetail.includes('<body');
    const looksLikeMissingTable = lowerDetail.includes('programmingerror')
      || lowerDetail.includes('operationalerror')
      || lowerDetail.includes('no such table')
      || lowerDetail.includes('does not exist');
    if (status === 404) return `${fallback}。没有找到对应的原始文件或服务，请先刷新文件列表；如果刚更新代码，请重启服务。`;
    if (status >= 500 && looksLikeMissingTable) return `${fallback}。DataHub 数据表结构异常，请联系管理员检查数据存储迁移状态。`;
    if (status >= 500 || looksLikeHtml) return `${fallback}。后端返回了调试错误页，请查看后端控制台日志。`;
    return detail.slice(0, 180);
  }
  if (status === 404 && detail?.detail) return `${fallback}。没有找到这个原始文件，请刷新文件列表后再试。`;
  if (detail?.detail) return String(detail.detail);
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  if (status >= 500) return `${fallback}。后端服务发生异常，请查看服务日志中的具体原因。`;
  return error?.message || fallback;
}

export default function DataHubPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentProject } = useProjectStore();
  const { user } = useAuthStore();
  const canManage = Boolean(user && user.role !== 'viewer');
  const [sources, setSources] = useState<DataHubSource[]>([]);
  const [datasets, setDatasets] = useState<DataHubDataset[]>([]);
  const [inventorySources, setInventorySources] = useState<DataHubSource[]>([]);
  const [inventoryDatasets, setInventoryDatasets] = useState<DataHubDataset[]>([]);
  const [applications, setApplications] = useState<DataHubApplication[]>([]);
  const [businessInventory, setBusinessInventory] = useState<DataHubBusinessInventoryModule[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dataType, setDataType] = useState<DataHubType>('well_log');
  const [sourceName, setSourceName] = useState('');
  const [manualLayout, setManualLayout] = useState(false);
  const [sheetName, setSheetName] = useState('');
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  const [unitRow, setUnitRow] = useState<number | null>(null);
  const [dataStartRow, setDataStartRow] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [maxHeaderScanRows, setMaxHeaderScanRows] = useState(40);
  const [uploading, setUploading] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [columnOverrides, setColumnOverrides] = useState<Record<string, Partial<DataHubColumn>>>({});
  const [excludedSourceKeys, setExcludedSourceKeys] = useState<Set<string>>(() => new Set());
  const [rangeAction, setRangeAction] = useState<'null' | 'clip' | 'keep'>('keep');
  const [statMethod, setStatMethod] = useState<'none' | 'hampel' | 'iqr' | 'zscore'>('hampel');
  const [statAction, setStatAction] = useState<'null' | 'clip' | 'keep'>('keep');
  const [smoothEnabled, setSmoothEnabled] = useState(false);
  const [smoothMethod, setSmoothMethod] = useState<'mean' | 'median'>('mean');
  const [smoothWindow, setSmoothWindow] = useState(5);
  const [resampleEnabled, setResampleEnabled] = useState(false);
  const [resampleStep, setResampleStep] = useState(1);
  const [depthReference, setDepthReference] = useState<'MD' | 'TVD' | 'TVDSS' | ''>('MD');
  const [pressureReference, setPressureReference] = useState<DataHubProcessOptions['pressure_reference_location']>('UNKNOWN');
  const [primaryPressureSourceKey, setPrimaryPressureSourceKey] = useState('');
  const [primaryRateSourceKey, setPrimaryRateSourceKey] = useState('');
  const [manualStageNumber, setManualStageNumber] = useState<number | null>(null);
  const [readiness, setReadiness] = useState<DataHubReadiness | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [realWellAcceptance, setRealWellAcceptance] = useState<RealWellAcceptance | null>(null);
  const [realWellAcceptanceLoading, setRealWellAcceptanceLoading] = useState(false);
  const [identityConfirming, setIdentityConfirming] = useState(false);
  const [auditRows, setAuditRows] = useState<DataHubAuditRow[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(1000);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRefreshToken, setAuditRefreshToken] = useState(0);
  const [curveRefreshToken, setCurveRefreshToken] = useState(0);
  const [selectedAuditCells, setSelectedAuditCells] = useState<Set<string>>(() => new Set());
  const [draggingAuditCells, setDraggingAuditCells] = useState(false);
  const [cellDecisionSaving, setCellDecisionSaving] = useState(false);
  const [curveField, setCurveField] = useState('');
  const [curveData, setCurveData] = useState<DataHubCurveResponse | null>(null);
  const [curveLoading, setCurveLoading] = useState(false);
  const [selectedCurvePoint, setSelectedCurvePoint] = useState<DataHubCurvePoint | null>(null);
  const [curveEditValue, setCurveEditValue] = useState<number | null>(null);
  const [pendingCurveDecisions, setPendingCurveDecisions] = useState<Record<string, PendingCurveDecision>>({});
  const pendingCurveDecisionCount = Object.keys(pendingCurveDecisions).length;

  const selectedSource = useMemo(
    () => sources.find((item) => item.id === selectedSourceId) || null,
    [sources, selectedSourceId],
  );
  const selectedDataset = useMemo(
    () => datasets.find((item) => item.id === selectedDatasetId) || null,
    [datasets, selectedDatasetId],
  );
  const activeDataType = selectedDataset?.data_type || selectedSource?.data_type || dataType;
  const workbenchInputTarget = WORKBENCH_INPUT_TARGETS[activeDataType];
  const activeTab = location.pathname.endsWith('/process')
    ? 'process'
    : location.pathname.endsWith('/assets')
      ? 'assets'
      : location.pathname.endsWith('/manage')
        ? 'manage'
        : 'import';
  const isCatalogView = activeTab === 'manage' || activeTab === 'import' || activeTab === 'process';

  const refresh = async (preferred?: { sourceId?: number | null; datasetId?: number | null }) => {
    if (!currentProject) return;
    setInventoryLoading(true);
    try {
      const [srcRes, dsRes, allSrcRes, allDsRes, appRes, businessRes] = await Promise.all([
        datahubAPI.listSources(currentProject.id),
        datahubAPI.listDatasets(currentProject.id),
        datahubAPI.listSources(currentProject.id, true),
        datahubAPI.listDatasets(currentProject.id, true),
        datahubAPI.listApplications(currentProject.id),
        datahubAPI.getBusinessInventory(currentProject.id),
      ]);
      setLoadError(null);
      setSources(srcRes.data);
      setDatasets(dsRes.data);
      setInventorySources(allSrcRes.data);
      setInventoryDatasets(allDsRes.data);
      setApplications(appRes.data);
      setBusinessInventory(businessRes.data.modules);
      const preferredSourceId = preferred && 'sourceId' in preferred ? preferred.sourceId : selectedSourceId;
      const preferredDatasetId = preferred && 'datasetId' in preferred ? preferred.datasetId : selectedDatasetId;
      const nextSourceId = preferredSourceId && srcRes.data.some((item) => item.id === preferredSourceId)
        ? preferredSourceId
        : srcRes.data[0]?.id ?? null;
      const nextDatasetId = preferredDatasetId && dsRes.data.some((item) => item.id === preferredDatasetId && item.source === nextSourceId)
        ? preferredDatasetId
        : dsRes.data.find((item) => item.source === nextSourceId)?.id ?? null;
      setSelectedSourceId(nextSourceId);
      setSelectedDatasetId(nextDatasetId);
    } catch (error: any) {
      setLoadError(apiErrorMessage(error, '数据清单加载失败'));
    } finally {
      setInventoryLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [currentProject?.id]);

  useEffect(() => {
    setColumnOverrides({});
    setExcludedSourceKeys(new Set());
    if (selectedSource?.data_type) {
      setDataType(selectedSource.data_type);
      setDepthReference(defaultDepthReference(selectedSource.data_type));
      const pressureCandidates = selectedSource.columns.filter((column) => column.pressure_role && column.pressure_role !== 'NOT_PRESSURE');
      const preferredPressure = pressureCandidates.find((column) => column.pressure_role === 'WELLHEAD')
        || (pressureCandidates.length === 1 ? pressureCandidates[0] : null);
      const rateCandidates = selectedSource.columns.filter((column) => column.rate_role && column.rate_role !== 'NOT_RATE');
      setPrimaryPressureSourceKey(preferredPressure?.source_key || '');
      setPrimaryRateSourceKey(rateCandidates.length === 1 ? rateCandidates[0].source_key : '');
    }
  }, [selectedSource?.id]);

  useEffect(() => {
    setAuditPage(1);
    setSelectedAuditCells(new Set());
    setSelectedCurvePoint(null);
    setCurveData(null);
    setPendingCurveDecisions({});
  }, [selectedDatasetId]);

  useEffect(() => {
    const columns = selectedDataset?.columns || [];
    const validFields = new Set(reviewableCurveColumns(columns).map((column) => column.dataset_field));
    setCurveField((current) => !isCurvePreviewType(selectedDataset?.data_type)
      ? ''
      : current && validFields.has(current)
        ? current
        : defaultCurveField(columns));
  }, [selectedDatasetId, selectedDataset?.columns]);

  useEffect(() => {
    if (activeTab !== 'assets' || !selectedDatasetId || !curveField || !isCurvePreviewType(selectedDataset?.data_type)) {
      setCurveData(null);
      return;
    }
    let cancelled = false;
    const requestDatasetId = selectedDatasetId;
    const requestField = curveField;
    setCurveData(null);
    setCurveLoading(true);
    datahubAPI.getDatasetCurve(requestDatasetId, { field: requestField, max_points: 3000 })
      .then((res) => {
        if (!cancelled && res.data.id === requestDatasetId && res.data.field === requestField) {
          setCurveData(res.data);
          setSelectedCurvePoint((current) => current
            ? res.data.points.find((point) => point.row_index === current.row_index) || null
            : null);
        }
      })
      .catch((error) => {
        if (!cancelled) message.error(apiErrorMessage(error, '加载曲线失败'));
      })
      .finally(() => {
        if (!cancelled) setCurveLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, selectedDatasetId, curveField, curveRefreshToken]);

  useEffect(() => {
    if (!selectedDatasetId) {
      setAuditRows([]);
      setAuditTotal(0);
      return;
    }
    let cancelled = false;
    setAuditLoading(true);
    datahubAPI.getDatasetAudit(selectedDatasetId, {
      offset: (auditPage - 1) * auditPageSize,
      limit: auditPageSize,
    }).then((res) => {
      if (cancelled) return;
      setAuditRows(res.data.rows);
      setAuditTotal(res.data.row_count);
    }).catch((error) => {
      if (!cancelled) message.error(apiErrorMessage(error, '加载逐行检查数据失败'));
    }).finally(() => {
      if (!cancelled) setAuditLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedDatasetId, auditPage, auditPageSize, auditRefreshToken]);

  useEffect(() => {
    if (!selectedDatasetId) {
      setReadiness(null);
      return;
    }
    let cancelled = false;
    setReadinessLoading(true);
    datahubAPI.getDatasetReadiness(selectedDatasetId)
      .then((res) => { if (!cancelled && res.data.dataset_id === selectedDatasetId) setReadiness(res.data); })
      .catch((error) => { if (!cancelled) message.error(apiErrorMessage(error, '应用检查加载失败')); })
      .finally(() => { if (!cancelled) setReadinessLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDatasetId, auditRefreshToken]);

  useEffect(() => {
    if (!selectedDatasetId || selectedDataset?.data_type !== 'fracture') {
      setRealWellAcceptance(null);
      return;
    }
    let cancelled = false;
    setRealWellAcceptanceLoading(true);
    datahubAPI.getRealWellAcceptance(selectedDatasetId)
      .then((res) => { if (!cancelled && res.data.profile.datasetId === selectedDatasetId) setRealWellAcceptance(res.data); })
      .catch((error) => { if (!cancelled) message.error(apiErrorMessage(error, '加载现场数据验收结果失败')); })
      .finally(() => { if (!cancelled) setRealWellAcceptanceLoading(false); });
    return () => { cancelled = true; };
  }, [selectedDatasetId, selectedDataset?.data_type, auditRefreshToken]);

  useEffect(() => {
    if (!draggingAuditCells) return undefined;
    const stop = () => setDraggingAuditCells(false);
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, [draggingAuditCells]);

  useEffect(() => {
    if (!pendingCurveDecisionCount) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [pendingCurveDecisionCount]);

  const beforeUpload: UploadProps['beforeUpload'] = (nextFile) => {
    if (!canManage) return Upload.LIST_IGNORE;
    setFile(nextFile as File);
    if (!sourceName) setSourceName(nextFile.name);
    return false;
  };

  const buildParseRequest = () => ({
    data_type: dataType,
    sheet_name: manualLayout ? sheetName.trim() || undefined : undefined,
    header_row: manualLayout ? headerRow || undefined : undefined,
    unit_row: manualLayout ? unitRow || undefined : undefined,
    data_start_row: manualLayout ? dataStartRow || undefined : undefined,
    template_id: dataType === 'fracture' ? templateId || undefined : undefined,
    max_header_scan_rows: dataType === 'fracture' ? maxHeaderScanRows : undefined,
  });

  const upload = async () => {
    if (!currentProject) return void message.warning('请先选择项目。');
    if (!file) return void message.warning('请先选择文件。');
    if (manualLayout && !headerRow) return void message.warning('手动读取文件时，请填写字段名称所在行。');
    setUploading(true);
    try {
      const parseRequest = buildParseRequest();
      const res = await datahubAPI.uploadSource({
        project_id: currentProject.id,
        name: sourceName || file.name,
        file,
        ...parseRequest,
      });
      let parsedSource = res.data;
      const layoutAwareFormats = ['CSV', 'TXT', 'XLS', 'XLSX'];
      const missingLayoutMeta = layoutAwareFormats.includes(parsedSource.file_format) && !parsedSource.meta?.layout_mode;
      const shouldReparse = (manualLayout && parsedSource.meta?.layout_mode !== 'manual') || (!manualLayout && missingLayoutMeta);
      if (shouldReparse) {
        message.loading({ key: 'datahub-upload', content: '上传完成，正在按设置重新读取文件…', duration: 0 });
        const reparsed = await datahubAPI.reparseSource(parsedSource.id, parseRequest);
        parsedSource = reparsed.data;
      }
      message.success({ key: 'datahub-upload', content: `已读取 ${parsedSource.row_count} 行数据。` });
      setSelectedSourceId(parsedSource.id);
      clearUploadForm();
      await refresh({ sourceId: parsedSource.id, datasetId: null });
      navigate('/datahub/process');
    } catch (error: any) {
      message.error(apiErrorMessage(error, '上传或读取文件失败'));
    } finally {
      setUploading(false);
    }
  };

  const reparseSelectedSource = async () => {
    if (!selectedSource) return void message.warning('请先选择要重新读取的文件。');
    if (manualLayout && !headerRow) return void message.warning('手动读取文件时，请填写字段名称所在行。');
    setReparsing(true);
    try {
      const res = await datahubAPI.reparseSource(selectedSource.id, buildParseRequest());
      setSelectedSourceId(res.data.id);
      setSelectedDatasetId(null);
      message.success(`已重新读取 ${res.data.row_count} 行数据`);
      await refresh({ sourceId: res.data.id, datasetId: null });
    } catch (error: any) {
      message.error(apiErrorMessage(error, '重新读取文件失败'));
    } finally {
      setReparsing(false);
    }
  };

  const process = async () => {
    if (!selectedSource) return void message.warning('请先选择原始文件。');
    if (!selectedSource.is_complete) return void message.error('当前只读取了文件的一部分，不能生成可审核的数据版本。请拆分文件后重新导入。');
    setProcessing(true);
    try {
      const mapping = toMapping(selectedSource, columnOverrides, excludedSourceKeys);
      if (!mapping?.length) {
        setProcessing(false);
        return void message.warning('字段已全部删除，请至少保留一个可处理字段。');
      }
      const missingUnitFields = mapping.filter((column) => column.standard_field && column.target_unit && !column.source_unit);
      if (missingUnitFields.length) {
        setProcessing(false);
        return void message.warning(`请先确认原始单位：${missingUnitFields.map((column) => column.display_name || column.source_key).join('、')}`);
      }
      const axis = selectedSource.data_type === 'fracture' ? 'time_s' : selectedSource.data_type === 'survey' ? 'md' : 'depth';
      const options: DataHubProcessOptions = {
        mapping,
        range_action: rangeAction,
        statistical_method: statMethod,
        statistical_action: statAction,
        smoothing: { method: smoothEnabled ? smoothMethod : 'none', window: smoothWindow },
        resample: { enabled: selectedSource.data_type === 'fracture' ? false : resampleEnabled, axis, step: resampleStep },
        depth_reference: depthReference,
        pressure_reference_location: selectedSource.data_type === 'fracture' ? pressureReference : undefined,
        primary_pressure_source_key: selectedSource.data_type === 'fracture' ? primaryPressureSourceKey || undefined : undefined,
        primary_rate_source_key: selectedSource.data_type === 'fracture' ? primaryRateSourceKey || undefined : undefined,
        manual_stage_number: selectedSource.data_type === 'fracture' && manualStageNumber != null ? manualStageNumber : undefined,
      };
      const res = await datahubAPI.processSource(selectedSource.id, options);
      setSelectedDatasetId(res.data.dataset.id);
      message.success(`数据版本已生成，质量评分 ${res.data.dataset.quality_score}，请继续审核。`);
      await refresh({ sourceId: selectedSource.id, datasetId: res.data.dataset.id });
      navigate('/datahub/assets');
    } catch (error: any) {
      message.error(apiErrorMessage(error, '生成数据版本失败'));
    } finally {
      setProcessing(false);
    }
  };

  const executeDatasetApplication = async () => {
    if (!selectedDataset) return;
    const downstream = DATA_TYPES.find((item) => item.value === selectedDataset.data_type)?.downstream;
    if (!downstream) return;
    setPublishing(true);
    try {
      const res = await datahubAPI.applyDataset(selectedDataset.id);
      const mechanics = res.data.result?.mechanics;
      const suffix = mechanics?.status === 'waiting_for_inputs'
        ? `；岩石力学计算待补：${(mechanics.missing_inputs || []).join('、')}`
        : mechanics?.calculated_points
          ? `；已生成 ${mechanics.calculated_points} 个动态岩石力学点`
          : '';
      message.success(`${res.data.idempotent ? '该版本已是当前版本' : `数据应用成功，已更新 ${downstream}`}${suffix}`, 6);
      await refresh({ sourceId: selectedDataset.source, datasetId: selectedDataset.id });
      setAuditRefreshToken((value) => value + 1);
    } catch (error: any) {
      message.error(apiErrorMessage(error, '数据应用失败'));
    } finally {
      setPublishing(false);
    }
  };

  const openDatasetApplication = () => {
    if (!selectedDataset) return void message.warning('请先选择要审核的数据版本。');
    const downstream = DATA_TYPES.find((item) => item.value === selectedDataset.data_type)?.downstream;
    if (!downstream) return void message.warning('该类数据暂时不能自动提供给其他功能使用。');
    if (readinessLoading || !readiness) return void message.info('正在执行应用检查，请稍候。');

    const localBlockers = pendingCurveDecisionCount
      ? [{ code: 'pending-corrections', title: '异常校正', detail: `还有 ${pendingCurveDecisionCount} 个校正结果未保存，请先点击“保存本批”。`, blocking: true }]
      : [];
    const blockers = [...localBlockers, ...readiness.blockers];
    const content = (
      <DatasetApplicationDialog
        dataset={selectedDataset}
        readiness={readiness}
        blockers={blockers}
      />
    );

    if (blockers.length || !readiness.can_apply) {
      Modal.error({
        title: '当前版本暂不能应用',
        content,
        okText: '返回处理',
        width: 620,
      });
      return;
    }
    Modal.confirm({
      title: '确认应用此版本？',
      content,
      okText: readiness.warnings.length ? '确认并应用' : '确认应用',
      cancelText: '取消',
      width: 620,
      onOk: executeDatasetApplication,
    });
  };

  const deleteSourcePermanently = (source: DataHubSource) => {
    if (source.can_delete === false) return void message.warning('该文件关联当前版本，无法删除。');
    Modal.confirm({
      title: `永久删除「${source.name}」？`,
      content: `将删除原始文件、由它生成的 ${source.dataset_count || 0} 个数据版本、审核结果和应用记录。此操作不可恢复。`,
      okText: '永久删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await datahubAPI.deleteSource(source.id);
          if (selectedSourceId === source.id) setSelectedSourceId(null);
          message.success('原始文件及其未应用版本已永久删除');
          await refresh();
        } catch (error: any) {
          message.error(apiErrorMessage(error, '删除原始文件失败'));
        }
      },
    });
  };

  const deleteDatasetPermanently = (dataset: DataHubDataset) => {
    if (dataset.can_delete === false) return void message.warning('当前版本无法删除。');
    Modal.confirm({
      title: `永久删除「${dataset.name} v${dataset.version}」？`,
      content: '将删除该数据版本、审核结果和失败的应用记录；原始文件与其它版本不受影响。此操作不可恢复。',
      okText: '永久删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await datahubAPI.deleteDataset(dataset.id);
          if (selectedDatasetId === dataset.id) setSelectedDatasetId(null);
          message.success('未应用版本已永久删除');
          await refresh({ sourceId: dataset.source, datasetId: null });
        } catch (error: any) {
          message.error(apiErrorMessage(error, '删除数据版本失败'));
        }
      },
    });
  };

  const restoreSource = async (source: DataHubSource) => {
    try {
      await datahubAPI.restoreSource(source.id);
      message.success('已恢复到当前数据列表');
      await refresh();
    } catch (error: any) {
      message.error(apiErrorMessage(error, '恢复原始文件失败'));
    }
  };

  const restoreDataset = async (dataset: DataHubDataset) => {
    try {
      await datahubAPI.restoreDataset(dataset.id);
      message.success('已恢复到当前数据列表');
      await refresh();
    } catch (error: any) {
      message.error(apiErrorMessage(error, '恢复数据版本失败'));
    }
  };

  const confirmWellIdentity = () => {
    if (!selectedSource) return;
    let reason = '';
    Modal.confirm({
      title: '确认井身份冲突',
      content: (
        <div>
          <p>文件井名与当前项目井名不一致。只有确认文件确实属于当前井后，项目才能使用这个版本。请填写确认原因，便于日后查阅。</p>
          <Input.TextArea rows={3} placeholder="例如：文件使用旧井名，经项目负责人核对为同一口井" onChange={(event) => { reason = event.target.value; }} />
        </div>
      ),
      okText: '确认属于当前井',
      cancelText: '取消',
      onOk: async () => {
        if (reason.trim().length < 5) {
          message.warning('请填写不少于 5 个字符的确认原因。');
          throw new Error('reason_required');
        }
        setIdentityConfirming(true);
        try {
          await datahubAPI.confirmSourceIdentity(selectedSource.id, reason.trim());
          message.success('井身份冲突已记录人工确认。');
          await refresh({ sourceId: selectedSource.id, datasetId: selectedDatasetId });
          setAuditRefreshToken((value) => value + 1);
        } catch (error: any) {
          if (error?.message !== 'reason_required') message.error(apiErrorMessage(error, '井身份确认失败'));
          throw error;
        } finally {
          setIdentityConfirming(false);
        }
      },
    });
  };

  const patchColumn = (sourceKey: string, patch: Partial<DataHubColumn>) => {
    setColumnOverrides((current) => ({ ...current, [sourceKey]: { ...current[sourceKey], ...patch } }));
  };

  const toggleMappingColumn = (sourceKey: string) => {
    setExcludedSourceKeys((current) => {
      const next = new Set(current);
      if (next.has(sourceKey)) next.delete(sourceKey);
      else next.add(sourceKey);
      return next;
    });
  };

  const saveFracturingMappingTemplate = () => {
    if (!selectedSource || selectedSource.data_type !== 'fracture') return;
    let templateName = String(selectedSource.meta?.import_profile?.templateName || '字段设置');
    let vendorLabel = String(selectedSource.meta?.import_profile?.vendorName || 'UNSPECIFIED');
    Modal.confirm({
      title: '保存当前字段设置',
      content: (
        <div>
          <p className="datahub-muted">只保存每列对应的字段、单位、用途和文件读取方式，不保存文件中的实际数据。</p>
          <Input defaultValue={templateName} placeholder="设置名称" onChange={(event) => { templateName = event.target.value; }} style={{ marginBottom: 8 }} />
          <Input defaultValue={vendorLabel} placeholder="文件提供方（可选）" onChange={(event) => { vendorLabel = event.target.value; }} />
        </div>
      ),
      okText: '保存配置',
      onOk: () => {
        localStorage.setItem(MAPPING_TEMPLATE_KEY, JSON.stringify({
          templateName: templateName.trim() || '字段设置',
          vendorLabel: vendorLabel.trim() || 'UNSPECIFIED',
          version: '1.0.0',
          mapping: toMapping(selectedSource, columnOverrides, excludedSourceKeys) || [],
          pressureReference, primaryPressureSourceKey, primaryRateSourceKey,
          headerRowPreference: selectedSource.meta?.header_row_number || null,
          sheetPreference: selectedSource.meta?.selected_sheet_name || '',
        }));
        message.success('当前字段设置已保存。');
      },
    });
  };

  const loadFracturingMappingTemplate = () => {
    if (!selectedSource || selectedSource.data_type !== 'fracture') return;
    try {
      const template = JSON.parse(localStorage.getItem(MAPPING_TEMPLATE_KEY) || '{}') as {
        mapping?: NonNullable<DataHubProcessOptions['mapping']>;
        pressureReference?: DataHubProcessOptions['pressure_reference_location'];
        primaryPressureSourceKey?: string;
        primaryRateSourceKey?: string;
      };
      const available = new Set(selectedSource.columns.map((column) => column.source_key));
      const overrides: Record<string, Partial<DataHubColumn>> = {};
      (template.mapping || []).filter((item) => available.has(item.source_key)).forEach((item) => { overrides[item.source_key] = item; });
      setColumnOverrides(overrides);
      if (template.pressureReference) setPressureReference(template.pressureReference);
      if (template.primaryPressureSourceKey && available.has(template.primaryPressureSourceKey)) setPrimaryPressureSourceKey(template.primaryPressureSourceKey);
      if (template.primaryRateSourceKey && available.has(template.primaryRateSourceKey)) setPrimaryRateSourceKey(template.primaryRateSourceKey);
      message.success(`已把保存的设置应用到 ${Object.keys(overrides).length} 列。`);
    } catch { message.error('保存的字段设置无法使用。'); }
  };

  const restoreDeletedColumns = () => {
    setExcludedSourceKeys(new Set());
  };

  const selectAuditCell = (key: string, extend = false) => {
    setSelectedAuditCells((current) => {
      const next = extend ? new Set(current) : new Set<string>();
      next.add(key);
      return next;
    });
  };

  const applyCellDecision = async (decision: 'accept' | 'raw') => {
    if (!selectedDatasetId) return;
    if (pendingCurveDecisionCount) return void message.warning('请先保存曲线中尚未保存的修改，再操作逐行检查表。');
    const cells = Array.from(selectedAuditCells).map(parseCellKey).filter(Boolean) as AuditCellRef[];
    if (!cells.length) return void message.warning('请先选择需要处理的单元格。');
    setCellDecisionSaving(true);
    try {
      const res = await datahubAPI.applyDatasetCellDecisions(selectedDatasetId, { decision, cells });
      message.success(`已更新 ${res.data.updated_cells} 个单元格`);
      setSelectedAuditCells(new Set());
      setAuditRefreshToken((value) => value + 1);
      setCurveRefreshToken((value) => value + 1);
      await refresh();
    } catch (error: any) {
      message.error(apiErrorMessage(error, '更新单元格决策失败'));
    } finally {
      setCellDecisionSaving(false);
    }
  };

  const applyCurveDecision = (decision: CurveDecision) => {
    if (!selectedDatasetId || !selectedCurvePoint || !curveField) return;
    if (decision === 'edit' && curveEditValue === null) {
      return void message.warning('请输入修改后的数值。');
    }
    const point = selectedCurvePoint;
    const nextDecision: DataHubCurvePoint['decision'] = decision === 'accept'
      ? 'accepted'
      : decision === 'edit'
        ? 'modified'
        : decision === 'exclude'
          ? 'excluded'
          : 'raw';
    const nextProcessed = decision === 'exclude'
      ? null
      : decision === 'edit'
        ? curveEditValue
        : decision === 'raw'
          ? point.raw
          : point.processed;
    const updatedPoint = { ...point, processed: nextProcessed, decision: nextDecision };

    setPendingCurveDecisions((current) => ({
      ...current,
      [pendingCurveDecisionKey(curveField, point.row_index)]: {
        row_index: point.row_index,
        field: curveField,
        decision,
        value: decision === 'edit' ? curveEditValue : undefined,
      },
    }));
    setCurveData((current) => {
      if (!current || current.field !== curveField) return current;
      const isNewlyReviewedIssue = point.issues.length > 0 && !point.decision;
      return {
        ...current,
        points: current.points.map((item) => item.row_index === point.row_index ? updatedPoint : item),
        reviewed_issue_count: (current.reviewed_issue_count || 0) + (isNewlyReviewedIssue ? 1 : 0),
        unresolved_issue_count: Math.max(0, (current.unresolved_issue_count || 0) - (isNewlyReviewedIssue ? 1 : 0)),
      };
    });

    const nextIssue = curveData?.points.find((item) => (
      item.row_index > point.row_index && item.issues.length > 0 && !item.decision
    )) || curveData?.points.find((item) => (
      item.row_index !== point.row_index && item.issues.length > 0 && !item.decision
    ));
    setSelectedCurvePoint(nextIssue || updatedPoint);
    setCurveEditValue(nextIssue ? (nextIssue.processed ?? nextIssue.raw ?? null) : nextProcessed);
  };

  const savePendingCurveDecisions = async () => {
    if (!selectedDatasetId) return;
    const cells = Object.values(pendingCurveDecisions);
    if (!cells.length) return;
    setCellDecisionSaving(true);
    try {
      const res = await datahubAPI.applyDatasetCellDecisions(selectedDatasetId, {
        decision: 'batch',
        cells,
      });
      const changesForCurve = new Map(
        res.data.changes
          .filter((change) => change.field === curveField)
          .map((change) => [change.row_index, change]),
      );
      const reconcilePoint = (point: DataHubCurvePoint) => {
        const change = changesForCurve.get(point.row_index);
        if (!change) return point;
        const after = typeof change.after === 'number' && Number.isFinite(change.after) ? change.after : null;
        return { ...point, processed: after, decision: change.decision };
      };
      setCurveData((current) => current && current.field === curveField
        ? { ...current, points: current.points.map(reconcilePoint) }
        : current);
      setSelectedCurvePoint((current) => current ? reconcilePoint(current) : null);
      setPendingCurveDecisions({});
      setDatasets((current) => current.map((dataset) => dataset.id === selectedDatasetId ? {
        ...dataset,
        review_revision: res.data.review_revision,
        content_checksum: res.data.content_checksum,
      } : dataset));
      setAuditRefreshToken((value) => value + 1);
      message.success(`已一次提交 ${res.data.updated_cells} 个点，曲线无需重新加载`);
    } catch (error: any) {
      message.error(apiErrorMessage(error, '批量保存曲线校正失败'));
    } finally {
      setCellDecisionSaving(false);
    }
  };

  const discardPendingCurveDecisions = () => {
    if (!pendingCurveDecisionCount) return;
    setPendingCurveDecisions({});
    setSelectedCurvePoint(null);
    setCurveEditValue(null);
    setCurveRefreshToken((value) => value + 1);
  };

  const clearUploadForm = () => {
    setFile(null);
    setSourceName('');
    setSheetName('');
    setHeaderRow(null);
    setUnitRow(null);
    setDataStartRow(null);
    setTemplateId('');
    setMaxHeaderScanRows(40);
    setManualLayout(false);
  };

  const closeUploadModal = () => {
    if (uploading) return;
    clearUploadForm();
    navigate('/datahub/manage');
  };

  const closeProcessModal = () => {
    if (processing) return;
    navigate('/datahub/manage');
  };

  const typeExample = DATA_TYPE_EXAMPLES[dataType] ?? null;

  const uploadPanel = (
    <div className="datahub-upload-grid">
      <div className="datahub-panel">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="计算所需数据与校准数据分开管理"
          description={
            <div>
              <div><strong>计算所需数据：</strong>井轨迹、地层、岩石力学、段簇、泵注程序。</div>
              <div><strong>校准数据（可选）：</strong>微地震、DFIT / 小压、施工压力；缺失不会阻止 P3D 计算，但结果会标记为“预测结果，未校准”。</div>
            </div>
          }
        />
        <div className="datahub-panel-title">
          <h2>选择文件</h2>
          <Tag color="blue">CSV / XLSX / LAS / TXT</Tag>
        </div>
        <Form layout="vertical">
          <Form.Item label="数据类型">
            <Select
              value={dataType}
              options={DATA_TYPES.map((item) => ({ value: item.value, label: item.label }))}
              onChange={setDataType}
            />
          </Form.Item>
          {dataType === 'fracture' && (
            <div className="datahub-parse-grid">
              <Form.Item label="文件格式模板">
                <Select
                  value={templateId}
                  options={[
                    { value: '', label: '自动识别（识别不到时使用通用格式）' },
                    { value: 'GENERIC_FRACTURING', label: '通用压裂格式' },
                    { value: 'VENDOR_LIKE_ENGLISH_CSV', label: '英文 CSV 常用格式' },
                    { value: 'VENDOR_LIKE_CHINESE_WORKBOOK', label: '中文工作簿常用格式' },
                  ]}
                  onChange={setTemplateId}
                />
              </Form.Item>
              <Form.Item label="最多检查前几行来识别字段名">
                <InputNumber min={10} max={500} value={maxHeaderScanRows} onChange={(value) => setMaxHeaderScanRows(Number(value || 40))} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          )}
          <Form.Item label="数据名称">
            <Input value={sourceName} onChange={(event) => setSourceName(event.target.value)} placeholder="默认使用文件名" />
          </Form.Item>
          <Form.Item label="文件读取设置">
            <Space align="center" wrap>
              <Switch checked={manualLayout} disabled={!canManage} onChange={setManualLayout} />
              <span style={{ color: 'var(--text-secondary)' }}>手动指定工作表、字段行、单位行和数据起始行</span>
            </Space>
          </Form.Item>
          {manualLayout && (
            <div className="datahub-parse-grid">
              <Form.Item label="工作表">
                <Input
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  placeholder="默认第一个，可填名称或序号"
                />
              </Form.Item>
              <Form.Item label="字段行">
                <InputNumber min={1} disabled={!canManage} value={headerRow} onChange={(value) => setHeaderRow(value ? Number(value) : null)} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="单位行">
                <InputNumber min={1} disabled={!canManage} value={unitRow} onChange={(value) => setUnitRow(value ? Number(value) : null)} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="数据起始行">
                <InputNumber min={1} disabled={!canManage} value={dataStartRow} onChange={(value) => setDataStartRow(value ? Number(value) : null)} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          )}
          <Upload.Dragger
            disabled={!canManage}
            accept=".csv,.txt,.xls,.xlsx,.las"
            multiple={false}
            maxCount={1}
            beforeUpload={beforeUpload}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
            <p className="ant-upload-text">选择或拖入数据文件</p>
            <p className="ant-upload-hint">支持 CSV、XLSX、LAS 和 TXT，原始文件会一并保留</p>
          </Upload.Dragger>
          {file && <div className="datahub-selected-file">已选择：{file.name}</div>}
          <Form.Item label="数据名称（可选）" style={{ marginTop: 14 }}>
            <Input value={sourceName} onChange={(event) => setSourceName(event.target.value)} placeholder="默认使用文件名" />
          </Form.Item>
          {typeExample && (
            <details className="datahub-upload-disclosure">
              <summary>格式要求与 CSV 模板</summary>
              <div className="datahub-type-example">
                <Space wrap>
                  {typeExample.formats.map((format) => <Tag key={format}>{format}</Tag>)}
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadTypeTemplate(dataType, typeExample)}
                  >
                    下载 CSV 模板
                  </Button>
                </Space>
                <div className="datahub-type-example-note">{typeExample.note}</div>
                <Table
                  size="small"
                  dataSource={typeExample.sampleRows}
                  columns={typeExample.columns.map((column) => ({
                    title: column.unit ? `${column.label} (${column.unit})` : column.label,
                    dataIndex: column.value,
                    width: 140,
                  }))}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  rowKey={(_, index) => String(index)}
                  locale={{ emptyText: '该类型暂无标准列模板' }}
                />
              </div>
            </details>
          )}
          <details className="datahub-upload-disclosure">
            <summary>文件读取设置 <span>通常无需修改</span></summary>
            <div className="datahub-upload-advanced-body">
              <div className="datahub-upload-switch">
              <Switch checked={manualLayout} disabled={!canManage} onChange={setManualLayout} />
              <span style={{ color: 'var(--text-secondary)' }}>手动指定工作表、字段行、单位行和数据起始行</span>
              </div>
              {manualLayout && (
                <div className="datahub-parse-grid">
                  <Form.Item label="工作表">
                    <Input
                      value={sheetName}
                      onChange={(event) => setSheetName(event.target.value)}
                      placeholder="默认第一个，可填名称或序号"
                    />
                  </Form.Item>
                  <Form.Item label="字段行">
                    <InputNumber min={1} disabled={!canManage} value={headerRow} onChange={(value) => setHeaderRow(value ? Number(value) : null)} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="单位行">
                    <InputNumber min={1} disabled={!canManage} value={unitRow} onChange={(value) => setUnitRow(value ? Number(value) : null)} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="数据起始行">
                    <InputNumber min={1} disabled={!canManage} value={dataStartRow} onChange={(value) => setDataStartRow(value ? Number(value) : null)} style={{ width: '100%' }} />
                  </Form.Item>
                </div>
              )}
            </div>
          </details>
          <div className="datahub-actions">
            <Button disabled={!canManage} onClick={clearUploadForm}>清空</Button>
            <Button type="primary" icon={<CloudUploadOutlined />} loading={uploading} disabled={!canManage} onClick={upload}>
              上传并读取
            </Button>
          </div>
        </Form>
      </div>
      <div className="datahub-panel">
        <div className="datahub-panel-title">
          <h2>文件读取结果</h2>
          <Space>
            <span>{selectedSource ? `${selectedSource.row_count.toLocaleString()} 行` : '未选择文件'}</span>
            {selectedSource && (
              <>
                <Button size="small" icon={<ReloadOutlined />} loading={reparsing} disabled={!canManage || Boolean(selectedSource.latest_dataset_id)} onClick={reparseSelectedSource}>
                  按设置重新读取
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!canManage || selectedSource.can_delete === false}
                  title={selectedSource.can_delete === false ? '该文件关联当前版本，无法删除' : '永久删除原始文件及其未应用版本'}
                  onClick={() => deleteSourcePermanently(selectedSource)}
                >
                  删除
                </Button>
              </>
            )}
          </Space>
        </div>
        {selectedSource ? (
          <>
            <div className="datahub-preview-meta">
              <div><span>数据类型</span><strong>{dataTypeLabel(selectedSource.data_type)}</strong></div>
              <div><span>字段</span><strong>{selectedSource.columns.length} 个</strong></div>
              <div><span>格式</span><strong>{selectedSource.file_format}</strong></div>
              <div><span>状态</span><strong>{STATUS_META[selectedSource.status]?.label || selectedSource.status}</strong></div>
            </div>
            <ParseLayoutSummary source={selectedSource} />
            {!selectedSource.is_complete && (
              <Alert type="error" showIcon message="当前只读取了文件的一部分" description={`原文件约 ${selectedSource.original_row_count.toLocaleString()} 行，当前只保留 ${selectedSource.row_count.toLocaleString()} 行；为避免把不完整数据用于计算，不能用这个文件生成数据版本。`} style={{ marginBottom: 12 }} />
            )}
            {selectedSource.meta?.well_identity?.status === 'mismatch' && (
              <Alert
                type={selectedSource.meta.well_identity.confirmed ? 'warning' : 'error'}
                showIcon
                message={`井身份不一致：文件 ${selectedSource.meta.well_identity.file_well || '未知'} / 项目 ${selectedSource.meta.well_identity.target_well || '未知'}`}
                description={selectedSource.meta.well_identity.confirmed
                  ? `已由 ${selectedSource.meta.well_identity.confirmed_by || '项目成员'} 人工确认：${selectedSource.meta.well_identity.confirmation_reason || '-'}`
                  : '可以继续查看和生成数据版本，但确认井名之前，项目不能使用这个版本。'}
                action={!selectedSource.meta.well_identity.confirmed ? <Button size="small" loading={identityConfirming} disabled={!canManage} onClick={confirmWellIdentity}>核对并确认</Button> : undefined}
                style={{ marginBottom: 12 }}
              />
            )}
            <Warnings warnings={selectedSource.parse_warnings} />
            <PreviewTable rows={selectedSource.preview_rows} columns={selectedSource.columns} />
          </>
        ) : <div className="datahub-empty">上传或选择一个文件后查看读取结果</div>}
      </div>
    </div>
  );

  const importModalPanel = (
    <Form className="datahub-import-form" layout="vertical">
      <div className="datahub-import-section">
        <div className="datahub-import-section-head">
          <span className="datahub-import-step">1</span>
          <div>
            <strong>选择数据类型</strong>
            <p>系统会据此识别每列内容、单位，以及哪些功能会使用它</p>
          </div>
        </div>
        <Select
          size="large"
          value={dataType}
          options={DATA_TYPES.map((item) => ({ value: item.value, label: item.label }))}
          onChange={setDataType}
          aria-label="数据类型"
        />
      </div>

      <div className="datahub-import-section">
        <div className="datahub-import-section-head">
          <span className="datahub-import-step">2</span>
          <div>
            <strong>上传原始文件</strong>
            <p>支持 CSV、XLSX、LAS 和 TXT，原始文件会一并保留</p>
          </div>
        </div>
        <Upload.Dragger
          className={file ? 'has-file' : undefined}
          disabled={!canManage}
          accept=".csv,.txt,.xls,.xlsx,.las"
          multiple={false}
          maxCount={1}
          beforeUpload={beforeUpload}
          showUploadList={false}
        >
          {file ? (
            <div className="datahub-import-file-ready">
              <CheckCircleOutlined />
              <div>
                <strong>{file.name}</strong>
                <span>文件已选择，点击或拖入可替换</span>
              </div>
            </div>
          ) : (
            <>
              <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
              <p className="ant-upload-text">点击选择文件，或拖到这里</p>
              <p className="ant-upload-hint">单个文件，系统会自动识别字段行和单位</p>
            </>
          )}
        </Upload.Dragger>
        <Form.Item className="datahub-import-name" label="数据名称（可选）">
          <Input
            size="large"
            value={sourceName}
            onChange={(event) => setSourceName(event.target.value)}
            placeholder="默认使用文件名"
          />
        </Form.Item>
      </div>

      <div className="datahub-import-options">
        {typeExample && (
          <details className="datahub-upload-disclosure">
            <summary>查看格式要求和 CSV 模板</summary>
            <div className="datahub-type-example">
              <Space wrap>
                {typeExample.formats.map((format) => <Tag key={format}>{format}</Tag>)}
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadTypeTemplate(dataType, typeExample)}
                >
                  下载 CSV 模板
                </Button>
              </Space>
              <div className="datahub-type-example-note">{typeExample.note}</div>
              <Table
                size="small"
                dataSource={typeExample.sampleRows}
                columns={typeExample.columns.map((column) => ({
                  title: column.unit ? `${column.label} (${column.unit})` : column.label,
                  dataIndex: column.value,
                  width: 140,
                }))}
                pagination={false}
                scroll={{ x: 'max-content' }}
                rowKey={(_, index) => String(index)}
                locale={{ emptyText: '该类型暂无标准列模板' }}
              />
            </div>
          </details>
        )}
        <details className="datahub-upload-disclosure">
            <summary>文件读取设置 <span>通常无需修改</span></summary>
          <div className="datahub-upload-advanced-body">
            <div className="datahub-upload-switch">
              <Switch checked={manualLayout} disabled={!canManage} onChange={setManualLayout} />
              <span>手动指定工作表、字段行、单位行和数据起始行</span>
            </div>
            {manualLayout && (
              <div className="datahub-parse-grid">
                <Form.Item label="工作表">
                  <Input
                    value={sheetName}
                    onChange={(event) => setSheetName(event.target.value)}
                    placeholder="名称或序号"
                  />
                </Form.Item>
                <Form.Item label="字段行">
                  <InputNumber min={1} disabled={!canManage} value={headerRow} onChange={(value) => setHeaderRow(value ? Number(value) : null)} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="单位行">
                  <InputNumber min={1} disabled={!canManage} value={unitRow} onChange={(value) => setUnitRow(value ? Number(value) : null)} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="数据起始行">
                  <InputNumber min={1} disabled={!canManage} value={dataStartRow} onChange={(value) => setDataStartRow(value ? Number(value) : null)} style={{ width: '100%' }} />
                </Form.Item>
              </div>
            )}
          </div>
        </details>
      </div>

      <div className="datahub-import-footer">
        <span>上传完成后，下一步确认字段和单位</span>
        <Space>
          <Button disabled={uploading} onClick={closeUploadModal}>取消</Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={uploading}
            disabled={!canManage || !file}
            onClick={upload}
          >
            上传并读取
          </Button>
        </Space>
      </div>
    </Form>
  );

  const mappingColumns: ColumnsType<DataHubColumn> = [
    {
      title: '源字段', dataIndex: 'display_name', key: 'display_name', width: 170,
      render: (_, column) => (
        <div>
          <strong>{column.display_name}</strong>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            有效 {Math.round((column.valid_rate || 0) * 100)}%
            {column.confirmation_required && <Tag color="gold" style={{ marginLeft: 6 }}>需要确认</Tag>}
            {column.alias_status === 'ALIAS_AMBIGUOUS' && (
              <div style={{ marginTop: 3, color: 'var(--warning-color)' }}>
                存在多个匹配项 · {(column.candidate_roles || []).map(readableChannelRole).join(' / ')}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '标准字段', key: 'standard_field', width: 280,
      render: (_, column) => {
        const patch = columnOverrides[column.source_key] || {};
        const current = Object.prototype.hasOwnProperty.call(patch, 'standard_field')
          ? patch.standard_field
          : column.standard_field ?? undefined;
        const options = FIELD_OPTIONS[selectedSource?.data_type || dataType].map((item) => ({
          value: item.value,
          label: item.unit ? `${item.label} (${item.unit})` : item.label,
        }));
        const needsConfirmation = columnOverrides[column.source_key]?.confirmation_required
          ?? column.confirmation_required
          ?? false;
        const excluded = excludedSourceKeys.has(column.source_key);
        return (
          <div className="datahub-field-mapping-control">
            <Select
              allowClear
              disabled={!canManage || excluded}
              value={current || undefined}
              style={{ width: '100%' }}
              placeholder="保留为自定义字段"
              options={options}
              onChange={(value) => {
                const meta = FIELD_OPTIONS[selectedSource?.data_type || dataType].find((item) => item.value === value);
                const customField = `custom_${column.source_key.replace(/[^\w\u4e00-\u9fa5]+/g, '_').toLowerCase()}`;
                patchColumn(column.source_key, {
                  standard_field: value || null,
                  dataset_field: value || customField,
                  target_unit: meta?.unit ?? column.unit ?? '',
                  value_type: meta?.type || column.value_type || 'number',
                  confirmation_required: false,
                });
              }}
            />
            {needsConfirmation && current && (
              <Button size="small" type="link" onClick={() => patchColumn(column.source_key, { confirmation_required: false })}>
                确认匹配
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: '源单位', key: 'source_unit', width: 180,
      render: (_, column) => {
        const patch = columnOverrides[column.source_key] || {};
        const selectedField = Object.prototype.hasOwnProperty.call(patch, 'standard_field') ? patch.standard_field : column.standard_field;
        const target = String(
          columnOverrides[column.source_key]?.target_unit
          ?? FIELD_OPTIONS[selectedSource?.data_type || dataType].find((item) => item.value === selectedField)?.unit
          ?? column.target_unit
          ?? '',
        );
        const sourceUnit = String(columnOverrides[column.source_key]?.source_unit ?? column.unit ?? '');
        const excluded = excludedSourceKeys.has(column.source_key);
        return (
          <Select
            disabled={!canManage || excluded}
            showSearch
            allowClear
            value={sourceUnit || undefined}
            placeholder="选择源单位"
            style={{ width: '100%' }}
            options={unitOptions(column, target)}
            onChange={(source_unit) => patchColumn(column.source_key, { source_unit: source_unit || '' })}
          />
        );
      },
    },
    {
      title: '标准单位', key: 'target_unit', width: 160,
      render: (_, column) => {
        const patch = columnOverrides[column.source_key] || {};
        const selectedField = Object.prototype.hasOwnProperty.call(patch, 'standard_field') ? patch.standard_field : column.standard_field;
        const target = String(
          columnOverrides[column.source_key]?.target_unit
          ?? FIELD_OPTIONS[selectedSource?.data_type || dataType].find((item) => item.value === selectedField)?.unit
          ?? column.target_unit
          ?? '',
        );
        const sourceUnit = String(columnOverrides[column.source_key]?.source_unit ?? column.unit ?? '');
        return (
          <div className="datahub-unit-target">
            <strong>{target || '无量纲'}</strong>
            <span className={!sourceUnit && target ? 'missing' : undefined}>{!sourceUnit && target
              ? '待配置源单位'
              : sourceUnit && target && sourceUnit !== target
              ? '自动换算'
              : '无需换算'}</span>
          </div>
        );
      },
    },
    {
      title: '配置状态', key: 'usage_status', width: 112,
      render: (_, column) => {
        if (excludedSourceKeys.has(column.source_key)) return <Tag>不使用</Tag>;
        const patch = columnOverrides[column.source_key] || {};
        const selectedField = Object.prototype.hasOwnProperty.call(patch, 'standard_field') ? patch.standard_field : column.standard_field;
        const needsConfirmation = patch.confirmation_required ?? column.confirmation_required ?? false;
        const target = String(patch.target_unit ?? FIELD_OPTIONS[selectedSource?.data_type || dataType].find((item) => item.value === selectedField)?.unit ?? column.target_unit ?? '');
        const sourceUnit = String(patch.source_unit ?? column.unit ?? '');
        if (needsConfirmation) return <Tag color="gold">待确认</Tag>;
        if (target && !sourceUnit) return <Tag color="error">缺少单位</Tag>;
        if (selectedField) return <Tag color="success">已匹配</Tag>;
        return <Tag color="blue">自定义</Tag>;
      },
    },
    {
      title: '操作', key: 'actions', width: 92,
      render: (_, column) => {
        const excluded = excludedSourceKeys.has(column.source_key);
        return (
          <Button
            danger={!excluded}
            disabled={!canManage}
            size="small"
            icon={excluded ? <ReloadOutlined /> : <DeleteOutlined />}
            onClick={() => toggleMappingColumn(column.source_key)}
          >
            {excluded ? '恢复' : '排除'}
          </Button>
        );
      },
    },
  ];

  const processPanel = selectedSource ? (
    <>
      {selectedSource.data_type === 'fracture' && <div style={{ marginBottom: 8, textAlign: 'right' }}><Space><Button size="small" onClick={loadFracturingMappingTemplate}>使用已保存的字段设置</Button><Button size="small" onClick={saveFracturingMappingTemplate}>保存当前字段设置</Button></Space></div>}
      <div className="datahub-panel" style={{ marginBottom: 14 }}>
        <div className="datahub-panel-title">
          <div>
            <h2>字段定义</h2>
          </div>
          <span>启用 {selectedSource.columns.length - excludedSourceKeys.size} / 排除 {excludedSourceKeys.size}</span>
        </div>
        <Table
          size="small"
          rowKey="source_key"
          columns={mappingColumns}
          dataSource={selectedSource.columns}
          pagination={false}
          scroll={{ x: 1080 }}
          rowClassName={(column) => excludedSourceKeys.has(column.source_key) ? 'datahub-field-row-disabled' : ''}
        />
      </div>
      <div className="datahub-panel">
        <div className="datahub-panel-title">
          <h2>处理规则</h2>
          <SettingOutlined />
        </div>
        <Alert
          type="warning"
          showIcon
          message="默认策略：标记异常并保留原值"
          description="曲线平滑默认关闭。"
          style={{ marginBottom: 12 }}
        />
        <div className="datahub-controls">
          <div className="datahub-control datahub-control-wide">
            <label>越界值处理</label>
            <Segmented
              block
              disabled={!canManage}
              value={rangeAction}
              options={[{ value: 'null', label: '置空' }, { value: 'clip', label: '边界截断' }, { value: 'keep', label: '保留并标记' }]}
              onChange={(value) => setRangeAction(value as typeof rangeAction)}
            />
          </div>
          <div className="datahub-control datahub-control-wide">
            <label>异常检测</label>
            <Segmented
              block
              disabled={!canManage}
              value={statMethod}
              options={[{ value: 'hampel', label: 'Hampel（推荐）' }, { value: 'iqr', label: 'IQR' }, { value: 'zscore', label: 'Z-score' }, { value: 'none', label: '关闭' }]}
              onChange={(value) => setStatMethod(value as typeof statMethod)}
            />
          </div>
          <div className="datahub-control datahub-control-wide">
            <label>异常值处理</label>
            <Segmented
              block
              disabled={!canManage}
              value={statAction}
              options={[{ value: 'null', label: '置空' }, { value: 'clip', label: '边界截断' }, { value: 'keep', label: '保留并标记' }]}
              onChange={(value) => setStatAction(value as typeof statAction)}
            />
          </div>
          <div className="datahub-control">
            <label>曲线平滑</label>
            <Space.Compact block>
              <Button style={{ width: 64 }} disabled={!canManage} onClick={() => setSmoothEnabled(!smoothEnabled)}>{smoothEnabled ? '开启' : '关闭'}</Button>
              <Select
                disabled={!smoothEnabled}
                value={smoothMethod}
                style={{ width: 'calc(100% - 64px)' }}
                options={[{ value: 'mean', label: '滑动平均' }, { value: 'median', label: '中位数平滑' }]}
                onChange={setSmoothMethod}
              />
            </Space.Compact>
          </div>
          <div className="datahub-control">
            <label>平滑窗口</label>
            <InputNumber min={3} max={21} step={2} disabled={!canManage} value={smoothWindow} onChange={(value) => setSmoothWindow(Number(value || 5))} style={{ width: '100%' }} />
          </div>
          <div className="datahub-control">
            <label>重采样 {selectedSource.data_type === 'fracture' && <Tooltip title="压裂分析保留全部有效数据；显示降采样不影响计算数据。"><QuestionCircleOutlined /></Tooltip>}</label>
            <Space>
              <Switch checked={selectedSource.data_type === 'fracture' ? false : resampleEnabled} disabled={!canManage || selectedSource.data_type === 'fracture'} onChange={setResampleEnabled} />
              <InputNumber disabled={!canManage || !resampleEnabled || selectedSource.data_type === 'fracture'} min={0.01} step={0.5} value={resampleStep} onChange={(value) => setResampleStep(Number(value || 1))} />
            </Space>
          </div>
          {['well_log', 'survey', 'pressure', 'mechanics'].includes(selectedSource.data_type) && (
            <div className="datahub-control">
                <label>深度基准 <Tooltip title="MD 为沿井眼测量的深度，TVD 为垂直深度，TVDSS 为相对海平面的垂直深度。"><QuestionCircleOutlined /></Tooltip></label>
              <Segmented
                block
                disabled={!canManage}
                value={depthReference}
                options={['MD', 'TVD', 'TVDSS']}
                onChange={(value) => setDepthReference(value as typeof depthReference)}
              />
            </div>
          )}
          {selectedSource.data_type === 'fracture' && (
            <>
              <div className="datahub-control">
                <label>压力测量位置 <Tooltip title="泵压、油压、套压可能来自不同测点，请确认主要施工压力实际测量的位置。"><QuestionCircleOutlined /></Tooltip></label>
                <Select
                  disabled={!canManage}
                  value={pressureReference}
                  style={{ width: '100%' }}
                  options={['UNKNOWN', 'WELLHEAD', 'BOTTOMHOLE', 'PRE_PERFORATION', 'FRACTURE_ENTRY'].map((value) => ({ value, label: PRESSURE_LOCATION_LABELS[value] || value }))}
                  onChange={(value) => setPressureReference(value as DataHubProcessOptions['pressure_reference_location'])}
                />
              </div>
              <div className="datahub-control">
                <label>主要施工压力列 <Tooltip title="选择后续压裂分析要使用的压力数据列。"><QuestionCircleOutlined /></Tooltip></label>
                <Select
                  allowClear
                  disabled={!canManage}
                  value={primaryPressureSourceKey || undefined}
                  placeholder="请选择主施工压力列"
                  style={{ width: '100%' }}
                  options={selectedSource.columns
                    .filter((column) => column.pressure_role && column.pressure_role !== 'NOT_PRESSURE')
                    .map((column) => ({
                      value: column.source_key,
                      label: `${column.display_name} · ${PRESSURE_ROLE_LABELS[column.pressure_role || ''] || column.pressure_role}${column.auxiliary_channel ? ' · 辅助列' : ''}`,
                    }))}
                  onChange={(value) => setPrimaryPressureSourceKey(value || '')}
                />
              </div>
              <div className="datahub-control">
                <label>台阶识别使用的排量列 <Tooltip title="选择降排量或升排量分析要使用的主要排量数据列。"><QuestionCircleOutlined /></Tooltip></label>
                <Select
                  allowClear
                  disabled={!canManage}
                  value={primaryRateSourceKey || undefined}
                  placeholder="多排量列时必须选择"
                  style={{ width: '100%' }}
                  options={selectedSource.columns
                    .filter((column) => column.rate_role && column.rate_role !== 'NOT_RATE')
                    .map((column) => ({
                      value: column.source_key,
                      label: `${column.display_name} · ${RATE_ROLE_LABELS[column.rate_role || ''] || column.rate_role}${column.auxiliary_channel ? ' · 辅助列' : ''}`,
                    }))}
                  onChange={(value) => setPrimaryRateSourceKey(value || '')}
                />
              </div>
              <div className="datahub-control">
                <label>默认段号</label>
                <InputNumber disabled={!canManage} min={1} value={manualStageNumber} onChange={(value) => setManualStageNumber(value == null ? null : Number(value))} style={{ width: '100%' }} />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="datahub-process-footer" role="region" aria-label="字段确认操作">
        <Space wrap>
          <Button disabled={processing} onClick={closeProcessModal}>取消</Button>
          <Button onClick={restoreDeletedColumns} disabled={!canManage || !excludedSourceKeys.size}>恢复全部字段</Button>
          <Button icon={<ReloadOutlined />} disabled={!canManage} onClick={() => { setColumnOverrides({}); setExcludedSourceKeys(new Set()); }}>重新自动匹配</Button>
        </Space>
        <div className={`datahub-process-status ${selectedSource.is_complete ? 'ready' : ''}`}>
          <span className="datahub-status-dot" />
          <strong>{selectedSource.is_complete
            ? `${selectedSource.columns.length} 个字段已加载 · 配置可提交`
            : '文件数据不完整，无法生成审核版本'}</strong>
        </div>
        <Button type="primary" icon={<PlayCircleOutlined />} loading={processing} disabled={!canManage || !selectedSource.is_complete} onClick={process}>生成审核版本</Button>
      </div>
    </>
  ) : <div className="datahub-empty">请选择已导入的数据文件</div>;

  const datasetImmutable = Boolean(selectedDataset && (selectedDataset.status === 'published' || readiness?.already_applied));
  const selectedDatasetIsWorkbenchInput = isWorkbenchInputType(selectedDataset?.data_type);
  const versionReady = Boolean(readiness && (selectedDatasetIsWorkbenchInput ? readiness.ready_for_use : readiness.can_apply));
  const versionActionReason = !canManage
    ? '当前账号为只读模式'
    : readinessLoading || !readiness
      ? '正在执行应用检查'
      : !selectedDatasetIsWorkbenchInput && readiness.already_applied
        ? '当前版本不可重复应用'
        : selectedDatasetIsWorkbenchInput && !versionReady
            ? readiness.blockers.map((item) => readableBackendText(item.detail)).join('；') || '版本未通过应用检查'
            : '';
  const versionStatusText = readinessLoading || !readiness
    ? '正在执行应用检查…'
    : !selectedDatasetIsWorkbenchInput && readiness.already_applied
      ? '当前版本'
      : pendingCurveDecisionCount
        ? `请先保存 ${pendingCurveDecisionCount} 个异常校正结果`
        : versionReady
          ? selectedDatasetIsWorkbenchInput
            ? '专业输入检查通过 · 可以进入工作台使用'
            : readiness.warnings.length
              ? `通过 · ${readiness.warnings.length} 项警告待确认`
              : '检查通过 · 可应用'
          : `阻塞 · ${readiness.blockers.length} 项问题待处理`;

  const qualityKpis = selectedDataset ? (
    <div className="datahub-kpis">
      <div className="datahub-kpi"><div className="datahub-kpi-label">数据版本</div><div className="datahub-kpi-value">v{selectedDataset.version}</div></div>
      <div className="datahub-kpi"><div className="datahub-kpi-label">数据行数</div><div className="datahub-kpi-value">{selectedDataset.row_count.toLocaleString()}</div></div>
      <div className="datahub-kpi"><div className="datahub-kpi-label">质量评分</div><div className="datahub-kpi-value datahub-quality-score">{Math.round(selectedDataset.quality_score)}</div></div>
      <div className="datahub-kpi"><div className="datahub-kpi-label">待处理问题</div><div className="datahub-kpi-value">{readiness?.quality.issue_count ?? selectedDataset.summary?.issue_count ?? 0}</div></div>
    </div>
  ) : null;

  const qualityOverviewPanel = selectedDataset ? (
    <>
      {selectedDataset.data_type === 'fracture' && (
        <FieldDatasetValidation
          acceptance={realWellAcceptance}
          loading={realWellAcceptanceLoading}
          onOpenInterpretation={() => navigate('/fracture/simulation')}
        />
      )}
      <div className="datahub-panel">
        <div className="datahub-panel-meta"><span>源文件</span><strong>{selectedDataset.source_name}</strong></div>
        <ProcessSummary options={selectedDataset.summary?.options || {}} />
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          locale={{ emptyText: '未发现质量问题' }}
          dataSource={Object.entries(selectedDataset.summary?.issues_by_type || {}).map(([key, value]) => ({ key, value }))}
          columns={[
            { title: '问题类型', dataIndex: 'key', render: (value: string) => issueTypeLabel(value) },
            { title: '数量', dataIndex: 'value', width: 120 },
          ]}
        />
      </div>
    </>
  ) : null;

  const exceptionCorrectionPanel = selectedDataset ? (
    <>
      {datasetImmutable && (
        <Alert
          type="info"
          showIcon
          message="当前版本为只读状态"
          description="如需调整，请基于源文件生成新版本。"
          style={{ marginBottom: 14 }}
        />
      )}
      {isCurvePreviewType(selectedDataset.data_type) ? (
        <div className="datahub-panel" style={{ marginBottom: 14 }}>
          <CurveReviewPanel
            dataset={selectedDataset}
            field={curveField}
            curve={curveData}
            loading={curveLoading}
            selectedPoint={selectedCurvePoint}
            editValue={curveEditValue}
            saving={cellDecisionSaving}
            pendingCount={pendingCurveDecisionCount}
            readOnly={datasetImmutable || !canManage}
            onFieldChange={(value) => {
              if (pendingCurveDecisionCount) {
                message.warning('请先保存或撤销当前曲线中尚未保存的修改。');
                return;
              }
              setCurveField(value);
              setCurveData(null);
              setSelectedCurvePoint(null);
              setCurveEditValue(null);
            }}
            onSelectPoint={(point) => {
              setSelectedCurvePoint(point);
              setCurveEditValue(point.processed ?? point.raw ?? null);
            }}
            onEditValue={setCurveEditValue}
            onDecision={applyCurveDecision}
            onSavePending={() => void savePendingCurveDecisions()}
            onDiscardPending={discardPendingCurveDecisions}
            onReloadCurve={() => {
              setSelectedCurvePoint(null);
              setCurveEditValue(null);
              setCurveRefreshToken((value) => value + 1);
            }}
          />
        </div>
      ) : (
        <div className="datahub-panel" style={{ marginBottom: 14 }}>
          <div className="datahub-panel-title">
            <div>
              <h3>离散数据审核</h3>
            </div>
          </div>
          <Alert
            type="info"
            showIcon
            message={`${dataTypeLabel(selectedDataset.data_type)} 为离散测试/区间数据`}
            description={selectedDatasetIsWorkbenchInput
              ? 'value_MPa、density_min 等字段需要结合 test_type（测试类型）和区间逐行理解；深度不必按顺序排列，同一深度也可以有多条记录。检查通过后，可在工作台的专业输入面板中预览并用于相关计算。'
              : 'value_MPa、density_min 等字段需要结合 test_type（测试类型）和区间逐行理解，不能当作一条连续曲线处理；请确认每行的深度、类型和数值。'}
            style={{ marginBottom: 12 }}
          />
          {selectedDataset.data_type === 'pressure_test' && (
            <Space wrap style={{ marginBottom: 12 }}>
              {Object.entries((selectedDataset.preview_rows || []).reduce<Record<string, number>>((counts, row) => {
                const key = String(row.test_type || '未知');
                counts[key] = (counts[key] || 0) + 1;
                return counts;
              }, {})).map(([type, count]) => <Tag key={type} color="blue">{type} × {count}</Tag>)}
            </Space>
          )}
        </div>
      )}
      <div className="datahub-panel">
        <div className="datahub-panel-title">
          <div>
            <h3>数据明细</h3>
          </div>
        </div>
        <AuditReviewTable
          rows={auditRows}
          columns={selectedDataset.columns}
          options={selectedDataset.summary?.options || {}}
          loading={auditLoading}
          page={auditPage}
          pageSize={auditPageSize}
          total={auditTotal}
          selectedCells={selectedAuditCells}
          dragging={draggingAuditCells}
          saving={cellDecisionSaving || datasetImmutable || !canManage}
          onPageChange={(page, pageSize) => {
            setAuditPage(page);
            setAuditPageSize(pageSize);
            setSelectedAuditCells(new Set());
          }}
          onSelectCell={selectAuditCell}
          onDragStart={() => setDraggingAuditCells(true)}
          onApplyDecision={applyCellDecision}
          onClearSelection={() => setSelectedAuditCells(new Set())}
        />
      </div>
    </>
  ) : null;

  const datasetPanel = selectedDataset ? (
    <div className="datahub-quality-workspace">
      {qualityKpis}
      <section className="datahub-review-section" aria-labelledby="datahub-quality-overview-title">
        <div className="datahub-review-section-heading">
          <span>01</span>
          <div>
            <h2 id="datahub-quality-overview-title">质量概览</h2>
          </div>
        </div>
        {qualityOverviewPanel}
      </section>
      <section className="datahub-review-section" aria-labelledby="datahub-correction-title">
        <div className="datahub-review-section-heading">
          <span>02</span>
          <div>
            <h2 id="datahub-correction-title">异常处理</h2>
          </div>
        </div>
        {exceptionCorrectionPanel}
      </section>
      <div className="datahub-sticky-action" role="region" aria-label="应用数据版本">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (pendingCurveDecisionCount) {
              message.warning('请先保存或撤销当前曲线中尚未保存的修改。');
              return;
            }
            navigate('/datahub/manage');
          }}
        >返回数据清单</Button>
        <div className={`datahub-sticky-status ${versionReady ? 'ready' : ''} ${readiness?.already_applied ? 'current' : ''}`}>
          <span className="datahub-status-dot" />
          <strong>{versionStatusText}</strong>
        </div>
        <Tooltip title={versionActionReason || undefined}>
          <span className="datahub-primary-action-wrap">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={publishing || readinessLoading}
              disabled={Boolean(versionActionReason)}
              onClick={() => {
                if (!selectedDatasetIsWorkbenchInput) {
                  openDatasetApplication();
                  return;
                }
                if (workbenchInputTarget) navigate(workbenchInputTarget.route);
              }}
            >
              {selectedDatasetIsWorkbenchInput
                ? readiness?.ready_for_use ? workbenchInputTarget?.label : '专业输入未就绪'
                : readiness?.already_applied ? '当前版本' : '应用版本'}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  ) : <div className="datahub-empty">请先生成审核版本</div>;

  const inventoryPanel = (
    <DataInventoryPanel
      sources={inventorySources}
      datasets={inventoryDatasets}
      applications={applications}
      businessInventory={businessInventory}
      loading={inventoryLoading}
      canManage={canManage}
      onDeleteSource={deleteSourcePermanently}
      onDeleteDataset={deleteDatasetPermanently}
      onRestoreSource={(source) => void restoreSource(source)}
      onRestoreDataset={(dataset) => void restoreDataset(dataset)}
      onImportType={(nextType) => {
        clearUploadForm();
        setSelectedSourceId(null);
        setSelectedDatasetId(null);
        setDataType(nextType);
        setDepthReference(defaultDepthReference(nextType));
        navigate('/datahub/import');
      }}
      onOpenSource={(source) => {
        setSelectedSourceId(source.id);
        setSelectedDatasetId(source.latest_dataset_id || null);
        navigate('/datahub/process');
      }}
      onOpenDataset={(dataset) => {
        setSelectedSourceId(dataset.source);
        setSelectedDatasetId(dataset.id);
        navigate('/datahub/assets');
      }}
    />
  );

  const activeContextControl = activeTab === 'process' ? (
    <div className="datahub-task-context" aria-label="当前原始文件">
      <span>当前文件</span>
      <Select
        size="small"
        value={selectedSourceId || undefined}
        placeholder="选择已上传文件"
        options={sources.map((source) => ({
          value: source.id,
          label: `${source.name} · ${source.row_count.toLocaleString()} 行`,
        }))}
        onChange={(id) => {
          if (pendingCurveDecisionCount) {
            message.warning('请先保存或撤销当前曲线中尚未保存的修改。');
            return;
          }
          setSelectedSourceId(id);
          const source = sources.find((item) => item.id === id);
          setSelectedDatasetId(source?.latest_dataset_id || null);
        }}
      />
    </div>
  ) : activeTab === 'assets' ? (
    <div className="datahub-task-context" aria-label="审核版本">
      <span>审核版本</span>
      <Select
        size="small"
        value={selectedDatasetId || undefined}
        placeholder="选择数据版本"
        options={datasets.map((dataset) => ({
          value: dataset.id,
          label: `${dataset.name} v${dataset.version} · 质量 ${dataset.quality_score}`,
        }))}
        onChange={(id) => {
          if (pendingCurveDecisionCount) {
            message.warning('请先保存或撤销当前曲线中尚未保存的修改。');
            return;
          }
          setSelectedDatasetId(id || null);
          const dataset = datasets.find((item) => item.id === id);
          if (dataset) setSelectedSourceId(dataset.source);
        }}
      />
    </div>
  ) : null;

  const pageTitle = isCatalogView
    ? '数据清单'
    : '数据审核';

  if (!currentProject) return <div className="datahub-empty">请先选择项目</div>;

  return (
    <div className={`datahub-page ${isCatalogView ? 'inventory-view' : 'workflow-view'}`}>
      <header className="datahub-header">
        <div className="datahub-title">
          <div className="datahub-eyebrow">
            <span>项目数据</span>
            <span className="datahub-project-chip">{currentProject.well_name}</span>
          </div>
          <h1>{pageTitle}</h1>
        </div>
        <div className="datahub-header-actions">
          {!canManage && <Tag className="datahub-readonly-tag">只读模式</Tag>}
          {activeTab === 'assets' && activeContextControl}
          <Button icon={<ReloadOutlined />} onClick={() => void refresh()}>刷新</Button>
          {(activeTab === 'manage' || activeTab === 'import') && (
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              disabled={!canManage}
              onClick={() => {
                clearUploadForm();
                setSelectedSourceId(null);
                setSelectedDatasetId(null);
                navigate('/datahub/import');
              }}
            >导入数据</Button>
          )}
        </div>
      </header>

      <div className="datahub-shell">
        <section className="datahub-main">
          {loadError && (
            <Alert
              type="error"
              showIcon
              message="数据中心接口暂不可用"
              description={loadError}
              style={{ marginBottom: 12 }}
            />
          )}
          {isCatalogView ? (
            <>
              {inventoryPanel}
              {GEOMECHANICS_UI_ENABLED && (
                <details className="datahub-geomechanics-overview">
                  <summary>专业计算运行摘要</summary>
                  <div>
                    <OverviewModule
                      projectId={currentProject.id}
                      backendEnabled={GEOMECHANICS_UI_ENABLED}
                    />
                  </div>
                </details>
              )}
            </>
          ) : (
            <div className="datahub-workflow-content">{datasetPanel}</div>
          )}
        </section>
      </div>

      <Modal
        rootClassName="datahub-import-modal-root datahub-process-modal-root"
        open={activeTab === 'process'}
        width={1180}
        title={(
          <div className="datahub-import-modal-title">
            <strong>字段配置</strong>
          </div>
        )}
        footer={null}
        closable={!processing}
        keyboard={!processing}
        maskClosable={!processing}
        destroyOnClose
        onCancel={closeProcessModal}
      >
        <div className="datahub-process-modal-context">{activeContextControl}</div>
        {processPanel}
      </Modal>

      <Modal
        rootClassName="datahub-import-modal-root"
        open={activeTab === 'import'}
        width={680}
        title={(
          <div className="datahub-import-modal-title">
            <strong>导入数据</strong>
          </div>
        )}
        footer={null}
        closable={!uploading}
        keyboard={!uploading}
        maskClosable={!uploading}
        destroyOnClose
        onCancel={closeUploadModal}
      >
        {importModalPanel}
      </Modal>
    </div>
  );
}

const BUSINESS_CODE_BY_DATA_TYPE: Partial<Record<DataHubType, string>> = {
  well_log: 'logging',
  survey: 'survey',
  formation: 'formation',
  pressure: 'pressure',
  mechanics: 'mechanics',
};

type InventoryFilter = 'all' | 'attention' | 'ready' | 'missing';
type InventoryStatusKey = Exclude<InventoryFilter, 'all'>;
type InventoryStatusTone = 'ready' | 'attention' | 'muted' | 'error';

type DataInventoryRow = {
  key: DataHubType;
  label: string;
  downstream: string;
  sources: DataHubSource[];
  datasets: DataHubDataset[];
  currentSource: DataHubSource | null;
  currentDataset: DataHubDataset | null;
  latestApplication: DataHubApplication | null;
  business: DataHubBusinessInventoryModule | null;
  statusKey: InventoryStatusKey;
  statusLabel: string;
  statusTone: InventoryStatusTone;
  issueCount: number;
};

function DataInventoryPanel({
  sources,
  datasets,
  applications,
  businessInventory,
  loading,
  canManage,
  onDeleteSource,
  onDeleteDataset,
  onRestoreSource,
  onRestoreDataset,
  onImportType,
  onOpenSource,
  onOpenDataset,
}: {
  sources: DataHubSource[];
  datasets: DataHubDataset[];
  applications: DataHubApplication[];
  businessInventory: DataHubBusinessInventoryModule[];
  loading: boolean;
  canManage: boolean;
  onDeleteSource: (source: DataHubSource) => void;
  onDeleteDataset: (dataset: DataHubDataset) => void;
  onRestoreSource: (source: DataHubSource) => void;
  onRestoreDataset: (dataset: DataHubDataset) => void;
  onImportType: (dataType: DataHubType) => void;
  onOpenSource: (source: DataHubSource) => void;
  onOpenDataset: (dataset: DataHubDataset) => void;
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<InventoryFilter>('all');
  const [detailType, setDetailType] = useState<DataHubType | null>(null);

  const applicationTarget = (application: DataHubApplication | null) => {
    const labels: Record<string, string> = {
      logging: '单井测井', logging_and_mechanics: '单井测井 / 岩石力学', survey: '井轨迹',
      pressure: '压力剖面', mechanics: '岩石力学', formation: '地层分层',
    };
    const target = application?.result?.target;
    return labels[target] || target || '-';
  };

  const rows = useMemo<DataInventoryRow[]>(() => {
    const businessByCode = new Map(businessInventory.map((item) => [item.code, item]));
    const newestFirst = <T extends { created_at: string }>(items: T[]) => [...items].sort(
      (left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
    );

    return DATA_TYPES.map((definition) => {
      const typeSources = newestFirst(sources.filter((source) => source.data_type === definition.value));
      const typeDatasets = [...datasets.filter((dataset) => dataset.data_type === definition.value)].sort(
        (left, right) => right.version - left.version
          || new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
      );
      const currentDataset = typeDatasets.find((dataset) => !dataset.archived_at) || null;
      const linkedSource = currentDataset
        ? typeSources.find((source) => source.id === currentDataset.source && !source.archived_at)
        : null;
      const currentSource = linkedSource || typeSources.find((source) => !source.archived_at) || null;
      const datasetIds = new Set(typeDatasets.map((dataset) => dataset.id));
      const latestApplication = newestFirst(
        applications.filter((application) => datasetIds.has(application.dataset)),
      )[0] || null;
      const businessCode = BUSINESS_CODE_BY_DATA_TYPE[definition.value];
      const business = businessCode ? businessByCode.get(businessCode) || null : null;
      const issueCount = Object.values(currentDataset?.summary?.issues_by_type || {}).reduce<number>(
        (total, value) => total + (Number(value) || 0), 0,
      );

      let statusKey: InventoryStatusKey = 'missing';
      let statusLabel = '未导入';
      let statusTone: InventoryStatusTone = 'muted';

      if (currentDataset) {
        if (currentDataset.status === 'failed' || latestApplication?.status === 'failed') {
          statusKey = 'attention';
          statusLabel = currentDataset.status === 'failed' ? '处理失败' : '应用失败';
          statusTone = 'error';
        } else if (latestApplication?.status === 'processing') {
          statusKey = 'attention'; statusLabel = '正在更新其他功能'; statusTone = 'attention';
        } else if (
          currentDataset.status === 'published' || currentDataset.has_applied_data
          || latestApplication?.status === 'applied' || Boolean(business?.datahub_records)
        ) {
          statusKey = 'ready';
          statusLabel = currentDataset.status === 'published' || currentDataset.has_applied_data || latestApplication?.status === 'applied'
            ? '当前版本' : '已有可用数据';
          statusTone = 'ready';
        } else if (currentDataset.quality_score < 80 || issueCount > 0) {
          statusKey = 'attention'; statusLabel = '需检查'; statusTone = 'attention';
        } else {
          statusKey = 'attention'; statusLabel = '待应用'; statusTone = 'attention';
        }
      } else if (currentSource) {
        statusKey = 'attention';
        statusLabel = currentSource.status === 'failed'
          ? '导入失败' : currentSource.status === 'uploaded' ? '正在读取文件' : '待确认字段';
        statusTone = currentSource.status === 'failed' ? 'error' : 'attention';
      } else if (business?.datahub_records) {
        statusKey = 'ready'; statusLabel = '其他页面已有数据'; statusTone = 'ready';
      } else if (business?.legacy_records) {
        statusKey = 'attention'; statusLabel = '缺少原始文件'; statusTone = 'attention';
      }

      return {
        key: definition.value, label: definition.label, downstream: definition.downstream || '项目资料',
        sources: typeSources, datasets: typeDatasets, currentSource, currentDataset, latestApplication,
        business, statusKey, statusLabel, statusTone, issueCount,
      };
    });
  }, [sources, datasets, applications, businessInventory]);

  const counts = {
    ready: rows.filter((row) => row.statusKey === 'ready').length,
    attention: rows.filter((row) => row.statusKey === 'attention').length,
    missing: rows.filter((row) => row.statusKey === 'missing').length,
  };
  const filteredRows = filter === 'all' ? rows : rows.filter((row) => row.statusKey === filter);
  const detailRow = rows.find((row) => row.key === detailType) || null;
  const detailDatasetIds = new Set(detailRow?.datasets.map((dataset) => dataset.id) || []);
  const detailApplications = [...applications]
    .filter((application) => detailDatasetIds.has(application.dataset))
    .sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());

  const statusNode = (row: DataInventoryRow) => (
    <span className={`datahub-status datahub-status-${row.statusTone}`}>
      <i aria-hidden="true" />{row.statusLabel}
    </span>
  );

  const openPrimary = (row: DataInventoryRow) => {
    setDetailType(null);
    if (row.currentDataset) onOpenDataset(row.currentDataset);
    else if (row.currentSource) onOpenSource(row.currentSource);
    else if (row.business?.total_records) navigate(row.business.route);
    else onImportType(row.key);
  };

  const columns: ColumnsType<DataInventoryRow> = [
    {
      title: '数据类型', key: 'type', width: 180,
      render: (_, row) => (
        <div className="datahub-catalog-type">
          <strong>{row.label}</strong>
          <small>{row.sources.length || row.datasets.length
            ? `${row.sources.length} 个原始文件 · ${row.datasets.length} 个数据版本` : '尚未导入数据'}</small>
        </div>
      ),
    },
    {
      title: '当前数据', key: 'current', width: 300,
      render: (_, row) => row.currentDataset ? (
        <div className="datahub-current-data">
          <strong>{row.currentDataset.name} v{row.currentDataset.version}</strong>
          <small>{row.currentDataset.source_name} · {row.currentDataset.row_count.toLocaleString()} 行</small>
        </div>
      ) : row.currentSource ? (
        <div className="datahub-current-data">
          <strong>{row.currentSource.name}</strong>
          <small>{row.currentSource.file_format || '文件'} · {row.currentSource.row_count.toLocaleString()} 行</small>
        </div>
      ) : row.business?.total_records ? (
        <div className="datahub-current-data">
          <strong>{row.business.label}</strong>
          <small>{row.business.total_records.toLocaleString()} {row.business.unit} · 其他页面已有数据</small>
        </div>
      ) : <span className="datahub-table-empty">—</span>,
    },
    { title: '状态', key: 'status', width: 120, render: (_, row) => statusNode(row) },
    {
      title: '质量', key: 'quality', width: 180,
      render: (_, row) => row.currentDataset ? (
        <div className="datahub-quality">
          <span><strong>{row.currentDataset.quality_score}</strong> 分</span>
          <small>{row.issueCount ? `${row.issueCount.toLocaleString()} 项需关注` : '未发现明显问题'}</small>
        </div>
      ) : row.currentSource ? (
        <div className="datahub-quality">
          <span>尚未生成报告</span>
          <small>{row.currentSource.parse_warnings?.length
            ? `${row.currentSource.parse_warnings.length} 条文件读取提示` : '进入数据审核后检查'}</small>
        </div>
      ) : row.business?.legacy_records ? (
        <div className="datahub-quality">
          <span>缺少原始文件记录</span><small>{row.business.legacy_records.toLocaleString()} 条数据找不到导入来源</small>
        </div>
      ) : <span className="datahub-table-empty">—</span>,
    },
    {
      title: '用于', key: 'downstream',
      render: (_, row) => (
        <div className="datahub-usage">
          <span>{row.downstream}</span>
          {row.business?.total_records
            ? <small>业务模块已有 {row.business.total_records.toLocaleString()} {row.business.unit}</small> : null}
        </div>
      ),
    },
    {
      title: '', key: 'action', width: 88, align: 'right',
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          disabled={!canManage && !row.currentDataset && !row.currentSource && !row.business?.total_records}
          onClick={(event) => { event.stopPropagation(); openPrimary(row); }}
        >
          {row.currentDataset ? '数据审核'
            : row.currentSource ? '字段核实' : row.business?.total_records ? '查看' : '导入'}
        </Button>
      ),
    },
  ];

  return (
    <div className="datahub-inventory">
      <section className="datahub-catalog">
        <div className="datahub-catalog-head">
          <div>
            <h2>项目数据一览</h2>
            <p>汇总数据完整性与版本状态。</p>
          </div>
          <div className="datahub-catalog-summary" aria-label="项目数据状态汇总">
            <span><i className="ready" /><strong>{counts.ready}</strong> 已有数据</span>
            <span><i className="attention" /><strong>{counts.attention}</strong> 待处理</span>
            <span><i className="missing" /><strong>{counts.missing}</strong> 未导入</span>
          </div>
        </div>

        <div className="datahub-catalog-toolbar">
          <Segmented
            value={filter}
            onChange={(value) => setFilter(value as InventoryFilter)}
            options={[
              { value: 'all', label: `全部 ${rows.length}` },
              { value: 'attention', label: `待处理 ${counts.attention}` },
              { value: 'ready', label: `已有数据 ${counts.ready}` },
              { value: 'missing', label: `未导入 ${counts.missing}` },
            ]}
          />
          <span>点击数据项查看文件、版本与应用记录</span>
        </div>

        <Table<DataInventoryRow>
          className="datahub-catalog-table"
          size="middle"
          rowKey="key"
          loading={loading}
          pagination={false}
          dataSource={filteredRows}
          columns={columns}
          scroll={{ x: 1040 }}
          onRow={(row) => ({
            tabIndex: 0,
            onClick: () => setDetailType(row.key),
            onKeyDown: (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setDetailType(row.key);
              }
            },
          })}
        />
      </section>

      <Drawer
        className="datahub-detail-drawer"
        width={560}
        open={Boolean(detailRow)}
        onClose={() => setDetailType(null)}
        title={detailRow ? `${detailRow.label}详情` : '数据详情'}
        extra={detailRow && canManage ? (
          <Button type="primary" size="small" onClick={() => { setDetailType(null); onImportType(detailRow.key); }}>
            导入新文件
          </Button>
        ) : null}
      >
        {detailRow && (
          <div className="datahub-detail-content">
            <div className="datahub-detail-summary">
              <div><span>当前状态</span>{statusNode(detailRow)}</div>
              <div>
                <span>当前数据</span>
                <strong>{detailRow.currentDataset
                  ? `${detailRow.currentDataset.name} v${detailRow.currentDataset.version}`
                  : detailRow.currentSource?.name || detailRow.business?.label || '尚未导入'}</strong>
              </div>
              <div><span>主要用途</span><strong>{detailRow.downstream}</strong></div>
            </div>

            {detailRow.business?.legacy_records ? (
              <Alert
                type="warning"
                showIcon
                message={`${detailRow.business.legacy_records.toLocaleString()} 条业务数据没有来源记录`}
                description={detailRow.business.legacy_detail?.note || '这些数据仍可使用，但无法追溯到原始文件和处理版本。'}
              />
            ) : null}

            {detailRow.business?.total_records ? (
              <section className="datahub-detail-section">
                <div className="datahub-detail-section-title">
                  <h3>其他页面中的数据</h3><span>{detailRow.business.total_records.toLocaleString()} {detailRow.business.unit}</span>
                </div>
                <div className="datahub-business-link">
                  <div>
                    <strong>{detailRow.business.label}</strong>
                    <small>{detailRow.business.datahub_records.toLocaleString()} 条有原始文件记录 · {detailRow.business.legacy_records.toLocaleString()} 条找不到导入来源</small>
                  </div>
                  <Button size="small" onClick={() => navigate(detailRow.business!.route)}>打开模块</Button>
                </div>
              </section>
            ) : null}

            <section className="datahub-detail-section">
              <div className="datahub-detail-section-title"><h3>数据版本</h3><span>{detailRow.datasets.length}</span></div>
              {detailRow.datasets.length ? detailRow.datasets.map((dataset) => {
                const sourceHidden = Boolean(sources.find((source) => source.id === dataset.source)?.archived_at);
                return (
                  <div className="datahub-record" key={dataset.id}>
                    <div className="datahub-record-main">
                      <div><strong>{dataset.name} v{dataset.version}</strong>{dataset.archived_at ? <span className="datahub-record-state">已隐藏</span> : null}</div>
                      <small>{dataset.row_count.toLocaleString()} 行 · 质量 {dataset.quality_score} · {new Date(dataset.created_at).toLocaleString()}</small>
                    </div>
                    <div className="datahub-record-actions">
                      {!dataset.archived_at
                        ? <Button size="small" type="link" onClick={() => onOpenDataset(dataset)}>数据审核</Button>
                        : <Button size="small" type="link" disabled={!canManage || sourceHidden} onClick={() => onRestoreDataset(dataset)}>恢复</Button>}
                      {!dataset.archived_at && dataset.can_delete !== false
                        ? <Button size="small" type="link" danger disabled={!canManage} onClick={() => onDeleteDataset(dataset)}>删除</Button>
                        : dataset.can_delete === false ? <span>已用于计算</span> : null}
                    </div>
                  </div>
                );
              }) : <div className="datahub-detail-empty">尚未生成处理版本</div>}
            </section>

            <section className="datahub-detail-section">
              <div className="datahub-detail-section-title"><h3>原始文件</h3><span>{detailRow.sources.length}</span></div>
              {detailRow.sources.length ? detailRow.sources.map((source) => (
                <div className="datahub-record" key={source.id}>
                  <div className="datahub-record-main">
                    <div><strong>{source.name}</strong>{source.archived_at ? <span className="datahub-record-state">已隐藏</span> : null}</div>
                    <small>{source.file_format || '文件'} · {source.row_count.toLocaleString()} 行 · {new Date(source.created_at).toLocaleString()}</small>
                  </div>
                  <div className="datahub-record-actions">
                    {!source.archived_at
                      ? <Button size="small" type="link" onClick={() => onOpenSource(source)}>字段核实</Button>
                      : <Button size="small" type="link" disabled={!canManage} onClick={() => onRestoreSource(source)}>恢复</Button>}
                    {!source.archived_at && source.can_delete !== false
                      ? <Button size="small" type="link" danger disabled={!canManage} onClick={() => onDeleteSource(source)}>删除</Button>
                      : source.can_delete === false ? <span>关联当前版本</span> : null}
                  </div>
                </div>
              )) : <div className="datahub-detail-empty">尚未上传原始文件</div>}
            </section>

            <section className="datahub-detail-section">
              <div className="datahub-detail-section-title"><h3>应用记录</h3><span>{detailApplications.length}</span></div>
              {detailApplications.length ? detailApplications.map((application) => (
                <div className="datahub-record datahub-application-record" key={application.id}>
                  <div className="datahub-record-main">
                    <div>
                      <strong>{application.dataset_name} v{application.dataset_version}</strong>
                      <span className={`datahub-record-state ${application.status}`}>
                        {application.status === 'applied' ? '成功' : application.status === 'failed' ? '失败' : '处理中'}
                      </span>
                    </div>
                    <small>{applicationTarget(application)} · {application.requested_by_name || '项目成员'} · {new Date(application.applied_at || application.created_at).toLocaleString()}</small>
                    {application.error_message ? <p>{readableBackendText(application.error_message)}</p> : null}
                  </div>
                </div>
              )) : <div className="datahub-detail-empty">暂无应用记录</div>}
            </section>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function Warnings({ warnings }: { warnings?: string[] }) {
  if (!warnings?.length) return null;
  return (
    <div className="datahub-warnings">
      {warnings.map((warning) => <Alert key={warning} type="warning" showIcon message={readableBackendText(readableDiagnostic(warning))} />)}
    </div>
  );
}

function ProcessSummary({ options }: { options: Record<string, any> }) {
  const smoothing = options.smoothing || {};
  const resample = options.resample || {};
  const actionNames: Record<string, string> = { keep: '保留并标记', null: '置空', clip: '边界截断' };
  const methodNames: Record<string, string> = {
    hampel: 'Hampel', iqr: 'IQR', zscore: 'Z-score', none: '关闭',
    mean: '滑动平均', median: '中位数平滑',
  };
  const axisNames: Record<string, string> = { depth: '深度', md: '测量深度', time_s: '时间' };
  const items = [
    ['越界值处理', actionNames[options.range_action] || actionNames.keep],
    ['异常检测', methodNames[options.statistical_method] || methodNames.none],
    ['异常值处理', actionNames[options.statistical_action] || actionNames.keep],
    ['曲线平滑', smoothing.method && smoothing.method !== 'none' ? `${methodNames[smoothing.method] || smoothing.method} · 窗口 ${smoothing.window || '-'}` : '关闭'],
    ['重采样', resample.enabled ? `${axisNames[resample.axis] || resample.axis || '-'} · 间隔 ${resample.step || '-'}` : '关闭'],
  ];
  return (
    <div className="datahub-process-summary">
      {items.map(([label, value]) => (
        <div key={label} className="datahub-process-chip">
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function CurveReviewPanel({
  dataset,
  field,
  curve,
  loading,
  selectedPoint,
  editValue,
  saving,
  pendingCount,
  readOnly,
  onFieldChange,
  onSelectPoint,
  onEditValue,
  onDecision,
  onSavePending,
  onDiscardPending,
  onReloadCurve,
}: {
  dataset: DataHubDataset;
  field: string;
  curve: DataHubCurveResponse | null;
  loading: boolean;
  selectedPoint: DataHubCurvePoint | null;
  editValue: number | null;
  saving: boolean;
  pendingCount: number;
  readOnly: boolean;
  onFieldChange: (field: string) => void;
  onSelectPoint: (point: DataHubCurvePoint) => void;
  onEditValue: (value: number | null) => void;
  onDecision: (decision: CurveDecision) => void;
  onSavePending: () => void;
  onDiscardPending: () => void;
  onReloadCurve: () => void;
}) {
  const chartRef = useRef<any>(null);
  const numericFields = reviewableCurveColumns(dataset.columns);
  const vertical = curve?.axis !== 'time_s';
  const unresolvedPoints = useMemo(
    () => curve?.points.filter((point) => point.issues.length > 0 && !point.decision) || [],
    [curve],
  );
  const chartOption = useMemo(() => {
    if (!curve) return {};
    const formatPoint = (value: number | null, axis: number, rowIndex: number) => (
      vertical ? [value, axis, rowIndex] : [axis, value, rowIndex]
    );
    const raw = curve.points.map((point) => formatPoint(point.raw, point.axis, point.row_index));
    const processed = curve.points.map((point) => formatPoint(point.processed, point.axis, point.row_index));
    const anomalies = curve.points
      .filter((point) => point.issues.length > 0 && !point.decision)
      .map((point) => formatPoint(point.processed ?? point.raw, point.axis, point.row_index));
    const reviewed = curve.points
      .filter((point) => Boolean(point.decision))
      .map((point) => formatPoint(point.processed ?? point.raw, point.axis, point.row_index));
    const selected = selectedPoint
      ? [formatPoint(selectedPoint.processed ?? selectedPoint.raw, selectedPoint.axis, selectedPoint.row_index)]
      : [];
    return {
      animation: false,
      grid: { left: 70, right: 38, top: 52, bottom: 48 },
      legend: { top: 8, data: ['原始曲线', '处理曲线', '待处理异常', '已审核点'] },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any[]) => {
          const first = params?.[0]?.data || [];
          const rowIndex = first[2];
          const point = curve.points.find((item) => item.row_index === rowIndex);
          if (!point) return '';
          return [
            `${escapeHtml(curve.axis.toUpperCase())}: ${escapeHtml(exactValue(point.axis))} ${escapeHtml(curve.axis_unit)}`,
            `原始文件: ${escapeHtml(exactValue(point.raw_original))} ${escapeHtml(curve.source_unit || curve.unit)}`,
            `统一单位后: ${escapeHtml(exactValue(point.raw))} ${escapeHtml(curve.unit)}`,
            `处理后: ${escapeHtml(exactValue(point.processed))} ${escapeHtml(curve.unit)}`,
            point.issues.length ? `自动检查: ${point.issues.map((issue) => escapeHtml(issueTypeLabel(issue.issue_type))).join('、')}` : '自动检查: 正常',
            `校正状态: ${escapeHtml(decisionLabel(point.decision))}`,
          ].join('<br/>');
        },
      },
      xAxis: {
        type: 'value',
        scale: true,
        name: vertical ? `${curve.field_label} (${curve.unit || '无量纲'})` : `${curve.axis.toUpperCase()} (${curve.axis_unit})`,
        nameLocation: 'middle',
        nameGap: 30,
        splitLine: { lineStyle: { color: '#edf0f3' } },
      },
      yAxis: {
        type: 'value',
        scale: true,
        inverse: vertical,
        name: vertical ? `${curve.axis.toUpperCase()} (${curve.axis_unit})` : `${curve.field_label} (${curve.unit || '无量纲'})`,
        nameLocation: 'middle',
        nameGap: 48,
        splitLine: { lineStyle: { color: '#edf0f3' } },
      },
      dataZoom: vertical
        ? [
            { type: 'inside', yAxisIndex: 0, start: 0, end: 100 },
            { type: 'slider', yAxisIndex: 0, start: 0, end: 100, right: 4, width: 14 },
          ]
        : [
            { type: 'inside', xAxisIndex: 0, start: 0, end: 100 },
            { type: 'slider', xAxisIndex: 0, start: 0, end: 100, bottom: 3, height: 16 },
          ],
      series: [
        {
          name: '原始曲线', type: 'line', data: raw, showSymbol: false, connectNulls: false,
          lineStyle: { color: '#8b96a3', width: 1, type: 'dashed' }, emphasis: { focus: 'series' },
        },
        {
          name: '处理曲线', type: 'line', data: processed, showSymbol: false, connectNulls: false,
          lineStyle: { color: '#167a72', width: 1.8 }, emphasis: { focus: 'series' },
        },
        {
          name: '待处理异常', type: 'scatter', data: anomalies, symbolSize: 9,
          itemStyle: { color: '#cf4b37', borderColor: '#fff', borderWidth: 1 }, z: 6,
        },
        {
          name: '已审核点', type: 'scatter', data: reviewed, symbol: 'diamond', symbolSize: 10,
          itemStyle: { color: '#27864a', borderColor: '#fff', borderWidth: 1 }, z: 7,
        },
        {
          name: '当前选择', type: 'scatter', data: selected, symbolSize: 14,
          itemStyle: { color: '#f3a712', borderColor: '#7f5200', borderWidth: 2 }, z: 8,
        },
      ],
    };
  }, [curve, selectedPoint, vertical]);

  const onEvents = {
    click: (params: any) => {
      const rowIndex = Array.isArray(params?.data) ? Number(params.data[2]) : NaN;
      const point = curve?.points.find((item) => item.row_index === rowIndex);
      if (point) onSelectPoint(point);
    },
  };

  const resetGlobalView = () => {
    const instance = chartRef.current?.getEchartsInstance?.();
    instance?.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
    instance?.resize();
  };

  const moveToIssue = (direction: -1 | 1) => {
    if (!unresolvedPoints.length) return;
    const currentIndex = selectedPoint
      ? unresolvedPoints.findIndex((point) => point.row_index === selectedPoint.row_index)
      : -1;
    const nextIndex = currentIndex < 0
      ? (direction > 0 ? 0 : unresolvedPoints.length - 1)
      : (currentIndex + direction + unresolvedPoints.length) % unresolvedPoints.length;
    onSelectPoint(unresolvedPoints[nextIndex]);
  };

  useEffect(() => {
    if (readOnly || saving || !selectedPoint) return undefined;
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      const key = event.key.toLowerCase();
      if (key === 'a') onDecision('accept');
      else if (key === 'r' && curve?.row_alignment !== 'resampled') onDecision('raw');
      else if (key === 'x') onDecision('exclude');
      else if (key === 'arrowdown' || key === 'arrowright') moveToIssue(1);
      else if (key === 'arrowup' || key === 'arrowleft') moveToIssue(-1);
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [curve?.row_alignment, onDecision, readOnly, saving, selectedPoint, unresolvedPoints]);

  return (
    <div className="datahub-curve-layout">
      <div className="datahub-curve-main">
        <div className="datahub-curve-toolbar">
          <Select
            value={field || undefined}
            placeholder="选择要检查的曲线"
            style={{ width: 300 }}
            options={numericFields.map((column) => ({
              value: column.dataset_field!,
              label: `${column.display_name || column.dataset_field} · ${column.target_unit || '无量纲'}`,
            }))}
            onChange={onFieldChange}
          />
          {curve && <Tag>{curve.sampled_count.toLocaleString()} 个显示点 / 共 {curve.row_count.toLocaleString()} 行</Tag>}
          {curve && <Tag color={curve.unresolved_issue_count ? 'error' : 'success'}>待处理 {curve.unresolved_issue_count || 0}</Tag>}
          {curve && <Tag color="success">已审核 {curve.reviewed_issue_count || 0}</Tag>}
          {pendingCount > 0 && <Tag color="gold">未保存 {pendingCount}</Tag>}
          {curve?.row_alignment === 'resampled' && <Tag color="gold">已重采样</Tag>}
          <Button size="small" disabled={!unresolvedPoints.length} onClick={() => moveToIssue(-1)}>上一个异常</Button>
          <Button size="small" disabled={!unresolvedPoints.length} onClick={() => moveToIssue(1)}>下一个异常</Button>
          <Button size="small" icon={<ReloadOutlined />} disabled={!curve} onClick={resetGlobalView}>全图</Button>
          <Button size="small" disabled={!curve?.unresolved_issue_count || pendingCount > 0 || saving} onClick={onReloadCurve}>刷新异常</Button>
          <Button size="small" type="primary" loading={saving} disabled={!pendingCount} onClick={onSavePending}>保存修改</Button>
          <Button size="small" disabled={!pendingCount || saving} onClick={onDiscardPending}>撤销修改</Button>
        </div>
        {field ? (
          <ReactECharts
            ref={chartRef}
            option={chartOption}
            onEvents={onEvents}
            showLoading={loading}
            style={{ height: 520, width: '100%' }}
            notMerge
          />
        ) : <div className="datahub-empty">当前数据版本没有可以显示的数值曲线</div>}
      </div>
      <aside className="datahub-point-editor">
        <div className="datahub-point-editor-title">
          <span><EditOutlined /> 点位处理</span>
          {selectedPoint?.decision && <Tag color="success">{decisionLabel(selectedPoint.decision)}</Tag>}
        </div>
        {selectedPoint ? (
          <>
            <div className="datahub-point-meta">
              <span>行号 <strong>{selectedPoint.display_index}</strong></span>
              <span>{curve?.axis.toUpperCase()} <strong>{selectedPoint.axis} {curve?.axis_unit}</strong></span>
              <span>原始文件 <strong title={exactValue(selectedPoint.raw_original)}>{displayValue(selectedPoint.raw_original)} {curve?.source_unit || curve?.unit}</strong></span>
              <span>统一单位后 <strong title={exactValue(selectedPoint.raw)}>{displayValue(selectedPoint.raw)} {curve?.unit}</strong></span>
              <span>当前值 <strong title={exactValue(selectedPoint.processed)}>{displayValue(selectedPoint.processed)} {curve?.unit}</strong></span>
            </div>
            {selectedPoint.issues.length ? (
              <Alert
                type="warning"
                showIcon
                message={selectedPoint.issues.map((issue) => issueTypeLabel(issue.issue_type)).join('、')}
                description={selectedPoint.issues.map((issue) => readableBackendText(issue.message)).join('；')}
              />
            ) : <Alert type="info" showIcon message="系统没有在这个点发现异常" />}
            {selectedPoint.decision && (
              <Alert
                type="success"
                showIcon
                message={`该点${decisionLabel(selectedPoint.decision)}`}
                description="绿色菱形表示已经确认；顶部若显示“尚未保存”，请完成这一批后统一保存。"
                style={{ marginTop: 8 }}
              />
            )}
            <div className="datahub-point-actions">
              <Button block type="primary" disabled={readOnly || saving} onClick={() => onDecision('accept')}>采用处理值（A）</Button>
              <Button block disabled={readOnly || saving || curve?.row_alignment === 'resampled'} onClick={() => onDecision('raw')}>恢复原始值（R）</Button>
              <Space.Compact block>
                <InputNumber
                  value={editValue}
                  disabled={readOnly || saving}
                  onChange={(value) => onEditValue(value === null ? null : Number(value))}
                  placeholder="输入修改值"
                  style={{ width: 'calc(100% - 72px)' }}
                />
                <Button disabled={readOnly || saving} onClick={() => onDecision('edit')}>确认修改</Button>
              </Space.Compact>
              <Button block danger disabled={readOnly || saving} onClick={() => onDecision('exclude')}>排除点位（X）</Button>
            </div>
            <div className="datahub-point-help">操作会先显示在曲线上，并自动跳到下一个异常点；完成一批后请统一保存。方向键可切换异常点，A 使用当前值、R 使用原始值、X 不使用该点。</div>
          </>
        ) : (
          <div className="datahub-point-placeholder">
            <strong>点击曲线上的点开始校正</strong>
            <span>红点是系统标出的异常；也可以点击任意正常点进行人工修改。</span>
          </div>
        )}
      </aside>
    </div>
  );
}

const SYNTHETIC_DIFF_REASON = '__derived_difference__';

function reasonKey(issue: DataHubAuditIssue) {
  if (issue.issue_type.startsWith('range_') || issue.issue_type === 'range') return 'range';
  if (issue.issue_type.startsWith('iqr_') || issue.issue_type === 'iqr') return 'iqr';
  if (issue.issue_type.startsWith('zscore_') || issue.issue_type === 'zscore') return 'zscore';
  return issue.issue_type || 'quality_issue';
}

function issueReasonSummary(issue: DataHubAuditIssue): Omit<AuditReason, 'id' | 'key' | 'label'> {
  const issueType = reasonKey(issue);
  const summaries: Record<string, string> = {
    null_marker: '原始值是空值或缺失值标记，例如 -、--、NA、-999.25 等，按空值处理。',
    non_numeric: '字段要求为数值，但原始内容无法转换为数字，按空值处理。',
    range: '数值超出这个系统字段的合理范围，系统已按当前设置处理。',
    iqr: '这个数值与整列大多数数据差异较大，系统已按“整体分布”设置处理。',
    zscore: '这个数值明显偏离整列平均值，系统已按“平均值偏差”设置处理。',
  };
  return {
    issue_type: issueType,
    action: issue.action || '-',
    message: summaries[issueType] || readableBackendText(issue.message) || '系统发现这个数值可能有问题，并已按当前设置处理。',
    severity: issue.severity || 'warning',
  };
}

function syntheticDiffReason(column: DataHubColumn, options: Record<string, any>): Omit<AuditReason, 'id' | 'key' | 'label'> & { key: string } {
  const sourceUnit = column.source_unit || column.unit || '原单位';
  const targetUnit = column.target_unit || column.unit || '目标单位';
  const conversion = column.conversion || 'none';
  if (conversion && conversion !== 'none') {
    return {
      key: 'unit_conversion',
      issue_type: 'unit_conversion',
      action: 'converted',
      message: `系统已统一单位，例如 ${sourceUnit} → ${targetUnit}；这只是单位转换，不代表原始数据异常。`,
      severity: 'info',
    };
  }
  const smoothing = options.smoothing || {};
  if (smoothing.method && smoothing.method !== 'none') {
    return {
      key: 'smoothing',
      issue_type: 'smoothing',
      action: 'smoothed',
      message: `已开启曲线平滑，每次参考相邻 ${smoothing.window || '-'} 个点；处理后的值可能与原始值不同。`,
      severity: 'info',
    };
  }
  const resample = options.resample || {};
  if (resample.enabled) {
    return {
      key: 'resample',
      issue_type: 'resample',
      action: 'resampled',
      message: `数据已按${resample.axis === 'time_s' ? '时间' : '深度'}间隔 ${resample.step || '-'} 重新取点，处理后的值可能由相邻点计算得到。`,
      severity: 'info',
    };
  }
  return {
    key: SYNTHETIC_DIFF_REASON,
    issue_type: 'derived',
    action: 'cleaned',
    message: '原始值与处理值不同，可能由单位转换、曲线平滑、重采样或人工修改产生。',
    severity: 'info',
  };
}

function decisionReason(decision?: string): (Omit<AuditReason, 'id' | 'key' | 'label'> & { key: string }) | null {
  if (decision === 'modified') {
    return {
      key: 'manual_edit', issue_type: 'manual_edit', action: 'modified', severity: 'info',
      message: '用户在曲线点编辑器或校正表中输入了新的确认值。',
    };
  }
  if (decision === 'excluded') {
    return {
      key: 'user_exclude', issue_type: 'user_exclude', action: 'excluded', severity: 'warning',
      message: '用户选择不使用这个点；该字段的数值已留空，但整行数据和原始文件仍会保留。',
    };
  }
  return null;
}

function buildAuditReasons(rows: DataHubAuditRow[], columns: DataHubColumn[], options: Record<string, any>) {
  const reasonMap = new Map<string, AuditReason>();
  const ensureReason = (key: string, partial: Omit<AuditReason, 'id' | 'key' | 'label'>) => {
    if (!reasonMap.has(key)) {
      const stableIndex = REASON_ORDER.indexOf(key);
      const id = stableIndex >= 0 ? stableIndex + 1 : REASON_ORDER.length + reasonMap.size + 1;
      reasonMap.set(key, {
        id,
        key,
        label: REASON_MARKS[id - 1] || `[${id}]`,
        ...partial,
      });
    }
    return reasonMap.get(key)!;
  };

  rows.forEach((row) => {
    row.issues.forEach((issue) => {
      ensureReason(reasonKey(issue), issueReasonSummary(issue));
    });
  });

  rows.forEach((row) => {
    columns.forEach((column) => {
      const field = column.dataset_field || column.source_key;
      const sourceKey = column.source_key;
      const hasIssue = row.issues.some((issue) => issue.field === field);
      const manualReason = decisionReason(row.decisions?.[field]);
      if (manualReason) {
        ensureReason(manualReason.key, manualReason);
      } else if (!hasIssue && !valuesEqual(row.raw?.[sourceKey], row.processed?.[field])) {
        const reason = syntheticDiffReason(column, options);
        ensureReason(reason.key, reason);
      }
    });
  });

  return { reasonMap, reasons: Array.from(reasonMap.values()) };
}

function AuditReviewTable({
  rows,
  columns,
  options,
  loading,
  page,
  pageSize,
  total,
  selectedCells,
  dragging,
  saving,
  onPageChange,
  onSelectCell,
  onDragStart,
  onApplyDecision,
  onClearSelection,
}: {
  rows: DataHubAuditRow[];
  columns: DataHubColumn[];
  options: Record<string, any>;
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  selectedCells: Set<string>;
  dragging: boolean;
  saving: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onSelectCell: (key: string, extend?: boolean) => void;
  onDragStart: () => void;
  onApplyDecision: (decision: 'accept' | 'raw') => void;
  onClearSelection: () => void;
}) {
  const activeColumns = columns.filter((column) => column.dataset_field || column.source_key);
  const { reasonMap, reasons } = useMemo(() => buildAuditReasons(rows, activeColumns, options), [rows, activeColumns, options]);
  const [onlyChanged, setOnlyChanged] = useState(false);
  const visibleRows = useMemo(() => {
    if (!onlyChanged) return rows;
    return rows.filter((row) => activeColumns.some((column) => {
      const field = column.dataset_field || column.source_key;
      return !valuesEqual(row.raw?.[column.source_key], row.processed?.[field])
        || row.issues.some((issue) => issue.field === field)
        || Boolean(row.decisions?.[field]);
    }));
  }, [activeColumns, onlyChanged, rows]);
  const tableWidth = 56 + activeColumns.length * 190;

  const tableColumns: ColumnsType<DataHubAuditRow> = [
    {
      title: '行号',
      dataIndex: 'display_index',
      width: 56,
      fixed: 'left',
      render: (value) => <span className="datahub-row-index">{value}</span>,
    },
    ...activeColumns.map((column) => {
      const field = column.dataset_field || column.source_key;
      const sourceKey = column.source_key;
      const unit = column.target_unit || column.source_unit || column.unit || '';
      const sourceUnit = column.source_unit || column.unit || '';
      return {
        title: (
          <span className="datahub-audit-field-title">
            {column.display_name}
            <br />
            <small>系统字段：{field}{unit ? ` / ${unit}` : ''}</small>
          </span>
        ),
        key: field,
        children: [
          {
            title: sourceUnit ? `原始 (${sourceUnit})` : '原始',
            key: `${field}:raw`,
            width: 74,
            render: (_: unknown, row: DataHubAuditRow) => (
              <span className="datahub-audit-plain-cell" title={exactValue(row.raw?.[sourceKey])}>{displayValue(row.raw?.[sourceKey])}</span>
            ),
          },
          {
            title: unit ? `处理后 (${unit})` : '处理后',
            key: `${field}:processed`,
            width: 80,
            render: (_: unknown, row: DataHubAuditRow) => {
              const key = cellKey(row.row_index, field);
              const rawValue = row.raw?.[sourceKey];
              const processedValue = row.processed?.[field];
              const issues = row.issues.filter((issue) => issue.field === field);
              const changed = !valuesEqual(rawValue, processedValue);
              const decision = row.decisions?.[field];
              const actionable = changed || issues.length > 0 || Boolean(decision);
              const selected = selectedCells.has(key);
              const classes = [
                'datahub-audit-value-cell',
                changed ? 'modified' : '',
                selected ? 'selected' : '',
                decision === 'raw' ? 'raw-kept' : '',
                decision === 'accepted' ? 'accepted' : '',
                decision === 'modified' ? 'manually-edited' : '',
                decision === 'excluded' ? 'excluded' : '',
              ].filter(Boolean).join(' ');
              const cell = (
                <div
                  className={classes}
                  onMouseDown={(event) => {
                    if (!actionable) return;
                    event.preventDefault();
                    onSelectCell(key, event.ctrlKey || event.metaKey);
                    onDragStart();
                  }}
                  onMouseEnter={() => {
                    if (dragging && actionable) onSelectCell(key, true);
                  }}
                >
                  <span title={exactValue(processedValue)}>{displayValue(processedValue)}</span>
                </div>
              );
              return cell;
            },
          },
          {
            title: '原因',
            key: `${field}:reason`,
            width: 36,
            render: (_: unknown, row: DataHubAuditRow) => {
              const issues = row.issues.filter((issue) => issue.field === field);
              const rawValue = row.raw?.[sourceKey];
              const processedValue = row.processed?.[field];
              const labels = Array.from(new Set(issues.map((issue) => reasonMap.get(reasonKey(issue))?.label).filter(Boolean)));
              const manualReason = decisionReason(row.decisions?.[field]);
              if (manualReason) {
                labels.push(reasonMap.get(manualReason.key)?.label);
              } else if (!labels.length && !valuesEqual(rawValue, processedValue)) {
                const reason = syntheticDiffReason(column, options);
                labels.push(reasonMap.get(reason.key)?.label);
              }
              const detail = issues.map((issue) => readableBackendText(issue.message)).filter(Boolean).join('\n');
              return labels.length ? <span className="datahub-reason-markers" title={detail || undefined}>{labels.join(' ')}</span> : <span className="datahub-muted">-</span>;
            },
          },
        ],
      };
    }),
  ];

  return (
    <div className="datahub-audit-layout">
      <div className="datahub-audit-table-wrap">
        <div className="datahub-audit-tools">
          <Space size={8} wrap>
            <Switch size="small" checked={onlyChanged} onChange={setOnlyChanged} />
            <span>仅看修改行</span>
            {onlyChanged && <Tag>{visibleRows.length.toLocaleString()} / {rows.length.toLocaleString()} 行</Tag>}
          </Space>
        </div>
        {selectedCells.size > 0 && (
          <div className="datahub-selection-toolbar" onMouseDown={(event) => event.stopPropagation()}>
            <strong>已选择 {selectedCells.size} 个单元格</strong>
            <Button size="small" type="primary" loading={saving} onClick={() => onApplyDecision('accept')}>
              采用处理值
            </Button>
            <Button size="small" danger loading={saving} onClick={() => onApplyDecision('raw')}>
              恢复原始值
            </Button>
            <Button size="small" onClick={onClearSelection}>清除</Button>
          </div>
        )}
        <Table<DataHubAuditRow>
          className="datahub-audit-table"
          size="small"
          rowKey="row_index"
          loading={loading}
          dataSource={visibleRows}
          columns={tableColumns}
          virtual
          sticky
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [200, 500, 1000],
            showTotal: (value) => `共 ${value.toLocaleString()} 行`,
            onChange: onPageChange,
          }}
          scroll={{ x: tableWidth, y: 560 }}
        />
      </div>
      <aside className="datahub-reason-panel">
        <div className="datahub-reason-title">变更说明</div>
        {reasons.length ? reasons.map((reason) => (
          <div className="datahub-reason-item" key={reason.key}>
            <Tag color={reason.severity === 'critical' ? 'red' : reason.severity === 'info' ? 'blue' : 'gold'}>{reason.label}</Tag>
            <strong>{issueTypeLabel(reason.issue_type)}</strong>
            <span>{reason.message}</span>
            <small>系统处理：{actionLabel(reason.action)}</small>
          </div>
        )) : <div className="datahub-muted">当前页没有数据修改记录</div>}
      </aside>
    </div>
  );
}

const ACCEPTANCE_STATUS_META: Record<string, { label: string; color: string }> = {
  PASS: { label: '通过', color: 'success' },
  PASS_WITH_WARNINGS: { label: '通过（有警告）', color: 'warning' },
  REVIEW_REQUIRED: { label: '需要人工确认', color: 'error' },
  FAIL: { label: '失败', color: 'error' },
  READY: { label: '可解释', color: 'success' },
  NOT_READY: { label: '不可解释', color: 'error' },
  NOT_EVALUATED: { label: '未评估', color: 'default' },
  NOT_AVAILABLE: { label: '不可用', color: 'default' },
  NOT_INTERPRETED: { label: '未解释', color: 'default' },
  NUMERICALLY_CLOSED: { label: '数值闭合', color: 'processing' },
  FIELD_VALIDATED: { label: '已有实测井底压力验证', color: 'success' },
  BHP_FIELD_VALIDATION_UNAVAILABLE: { label: '没有实测井底压力可验证', color: 'default' },
  APPROVED: { label: '已批准', color: 'success' },
  ACTIVE: { label: '当前版本', color: 'success' },
  DRAFT: { label: '草稿', color: 'default' },
};

function AcceptanceTag({ value }: { value?: string | null }) {
  const meta = ACCEPTANCE_STATUS_META[value || ''] || { label: value || '-', color: 'default' };
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

function FieldDatasetValidation({
  acceptance,
  loading,
  onOpenInterpretation,
}: {
  acceptance: RealWellAcceptance | null;
  loading: boolean;
  onOpenInterpretation: () => void;
}) {
  if (loading) return <div className="datahub-panel" style={{ marginBottom: 14 }}>正在检查现场数据…</div>;
  if (!acceptance) return null;
  const profile = acceptance.profile;
  const dimensionLabels: Record<string, string> = {
    mapping: '字段对应', units: '单位', time: '时间', stageMapping: '段号对应',
    pressureReference: '压力测量位置', rateSource: '主要排量列', qc: '异常检查',
    interpretation: '台阶识别', fieldFriction: '现场摩阻', bhp: '井底压力',
  };
  const checklistLabels: Record<string, string> = {
    WELL_IDENTITY: '井名', VENDOR_TEMPLATE: '文件格式模板', SHEET: '工作表', HEADER: '字段行', UNITS: '单位',
    PRESSURE_CHANNEL: '压力数据列', RATE_CHANNEL: '排量数据列', TIME_CONVENTION: '时间格式', TIMEZONE: '时区',
    STAGE_MAPPING: '段号', PUMP_PROGRAM_ALIGNMENT: '泵注程序对应', QC: '异常检查',
    STEP_INTERPRETATION_READINESS: '台阶识别准备情况', FIELD_FRICTION: '现场摩阻', MEASURED_BHP: '实测井底压力',
  };
  const columns: ColumnsType<RealWellStageAcceptance> = [
    { title: '段号', dataIndex: 'stage', width: 75, render: (value) => value ?? '未识别' },
    { title: '数据行数', dataIndex: 'rows', width: 95 },
    { title: '时间', dataIndex: 'time', width: 105, render: (value) => <AcceptanceTag value={value} /> },
    { title: '压力', dataIndex: 'pressure', width: 110, render: (value) => <AcceptanceTag value={value} /> },
    { title: '排量', dataIndex: 'rate', width: 105, render: (value) => <AcceptanceTag value={value} /> },
    { title: '台阶数', dataIndex: 'stepCount', width: 80 },
    { title: '台阶识别', dataIndex: 'interpretation', width: 125, render: (value) => <AcceptanceTag value={value} /> },
    { title: '现场摩阻', dataIndex: ['fieldFriction', 'status'], width: 130, render: (value) => <AcceptanceTag value={value} /> },
    { title: '井底压力', dataIndex: ['bhp', 'status'], width: 130, render: (value) => <AcceptanceTag value={value} /> },
    { title: '检查结果', dataIndex: 'status', width: 130, render: (value) => <AcceptanceTag value={value} /> },
  ];
  return (
    <div className="datahub-panel" style={{ marginBottom: 14 }}>
      <div className="datahub-panel-title">
        <div>
          <h2>现场数据检查</h2>
          <span>{profile.sourceFile} · 数据版本 v{profile.datasetVersion} · {profile.wellId || '未识别井号'}</span>
        </div>
        <Space>
          <AcceptanceTag value={acceptance.overallAcceptance} />
          <Button size="small" onClick={onOpenInterpretation}>查看台阶与摩阻分析</Button>
        </Space>
      </div>
      <div className="datahub-context-bar" style={{ marginBottom: 10 }}>
        <span>文件模板：{readableSystemValue(profile.vendorTemplate)} / v{profile.templateVersion}</span>
        <span>文件来源：{readableSystemValue(profile.dataSource)}</span>
        <span>工作表：{profile.sheetName || '-'}</span>
        <span>字段行：{profile.headerRow || '-'} / {readableConfidence(profile.headerConfidence)}</span>
        <span>压力位置：{PRESSURE_LOCATION_LABELS[profile.pressureReference] || profile.pressureReference} / {readableConfidence(profile.pressureRoleConfidence)}</span>
        <span>排量列识别：{readableConfidence(profile.rateRoleConfidence)}</span>
        <span>时间列：{readableSystemValue(profile.timeSource)}</span>
      </div>
      <Table
        size="small"
        rowKey={(row) => String(row.stage ?? 'UNRESOLVED')}
        pagination={false}
        scroll={{ x: 1120 }}
        dataSource={acceptance.stageAcceptance}
        columns={columns}
        expandable={{
          expandedRowRender: (row) => (
            <div className="datahub-context-bar">
              <span>压力位置：{PRESSURE_LOCATION_LABELS[row.pressureReference] || row.pressureReference}</span>
              <span>排量列：{RATE_ROLE_LABELS[row.rateSource?.rateRole || ''] || row.rateSource?.rateRole || '未确认'} / {row.rateSource?.datasetField || '-'}</span>
              <span>可用排量范围：{row.fieldFriction.validRateRange?.length ? row.fieldFriction.validRateRange.join('–') + ' m³/min' : '-'}</span>
              <span>摩阻规则版本：{row.fieldFriction.registryVersion ? `v${row.fieldFriction.registryVersion}` : '-'}</span>
              <span>井底压力误差：{row.bhp.pressureClosureErrorMpa == null ? '-' : `${compactNumber(row.bhp.pressureClosureErrorMpa)} MPa`}</span>
              <span>需要注意：{row.diagnostics.length ? row.diagnostics.map(readableDiagnostic).join('、') : '无'}</span>
            </div>
          ),
        }}
      />
      <div style={{ marginTop: 10 }}>
        <Space size={[6, 6]} wrap>
          {Object.entries(acceptance.dimensions).map(([key, value]) => (
            <span key={key}>{dimensionLabels[key] || key}: <AcceptanceTag value={value} /></span>
          ))}
        </Space>
      </div>
      <div className="datahub-context-bar" style={{ marginTop: 10 }}>
        <span>数据行数：{(profile.performanceSummary.publishedRowCount || 0).toLocaleString()}</span>
        <span>文件大小：{profile.performanceSummary.fileSize ? `${(profile.performanceSummary.fileSize / 1024 / 1024).toFixed(2)} MB` : '-'}</span>
        <span>读取耗时：{profile.performanceSummary.parseDurationMs == null ? '-' : `${compactNumber(profile.performanceSummary.parseDurationMs)} ms`}</span>
        <span>单位统一耗时：{profile.performanceSummary.normalizeDurationMs == null ? '-' : `${compactNumber(profile.performanceSummary.normalizeDurationMs)} ms`}</span>
        <span>异常检查耗时：{profile.performanceSummary.qcDurationMs == null ? '-' : `${compactNumber(profile.performanceSummary.qcDurationMs)} ms`}</span>
      </div>
      <div style={{ marginTop: 8 }}>
        <Space size={[6, 6]} wrap>
          {acceptance.intakeChecklist.map((item) => (
            <span key={item.code}>{checklistLabels[item.code] || item.code}：<AcceptanceTag value={item.status} /></span>
          ))}
        </Space>
      </div>
      {profile.warnings.length > 0 && (
        <Alert
          type={acceptance.overallAcceptance === 'FAIL' ? 'error' : 'warning'}
          showIcon
          message="需要注意"
          description={profile.warnings.map(readableDiagnostic).join('；')}
          style={{ marginTop: 10 }}
        />
      )}
      <div className="datahub-muted" style={{ marginTop: 8 }}>
        数据记录：版本 {profile.provenance.datasetId} / v{profile.provenance.datasetVersion} · 字段设置 v{profile.provenance.mappingVersion} · 规则库 {profile.provenance.registryVersions?.join(', ') || '-'}
      </div>
    </div>
  );
}

function DatasetApplicationDialog({
  dataset,
  readiness,
  blockers,
}: {
  dataset: DataHubDataset;
  readiness: DataHubReadiness;
  blockers: DataHubReadiness['blockers'];
}) {
  const hasBlockers = blockers.length > 0 || !readiness.can_apply;
  return (
    <div className="datahub-apply-dialog">
      <div className="datahub-apply-summary">
        <div><span>数据版本</span><strong>{dataset.name} v{dataset.version}</strong></div>
        <div><span>数据类型</span><strong>{dataTypeLabel(dataset.data_type)}</strong></div>
        <div><span>质量评分</span><strong>{Math.round(dataset.quality_score)}</strong></div>
      </div>
      {hasBlockers ? (
        <Alert
          type="error"
          showIcon
          message={`存在 ${Math.max(1, blockers.length)} 项问题，处理后才能应用`}
          description={blockers.length
            ? blockers.map((item) => `${readableBackendText(item.title)}：${readableBackendText(item.detail)}`).join('；')
            : '当前版本未通过应用检查。'}
        />
      ) : readiness.warnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={`仍有 ${readiness.warnings.length} 项提示`}
          description={readiness.warnings.map((item) => readableBackendText(item.detail)).join('；')}
        />
      ) : (
        <Alert
          type="success"
          showIcon
          message={`检查通过 · ${readiness.passed.length} 项校验已完成`}
          description="未发现影响应用的问题。"
        />
      )}
      <div className="datahub-apply-section-title">应用范围</div>
      <div className="datahub-apply-targets">
        {readiness.downstream.map((target) => (
          <div key={target.code} className={`datahub-downstream ${target.status}`}>
            <div><Tag color={target.status === 'ready' ? 'success' : target.status === 'waiting' ? 'gold' : 'error'}>{target.status === 'ready' ? '就绪' : target.status === 'waiting' ? '等待输入' : '阻断'}</Tag><strong>{target.label}</strong></div>
            <p>{readableBackendText(target.detail)}</p>
          </div>
        ))}
      </div>
      {!hasBlockers && (
        <div className="datahub-apply-impact">
          应用后，此版本将成为项目当前使用的数据版本，并更新以上关联功能。
        </div>
      )}
    </div>
  );
}

function ParseLayoutSummary({ source }: { source: DataHubSource }) {
  const meta = source.meta || {};
  if (!meta.layout_mode) {
    return (
      <Alert
        type="warning"
        showIcon
        message="这个文件没有保存读取位置"
        description="它可能是用旧版功能导入的。请点击右上角“按设置重新读取”，让系统按当前设置找到字段行、单位行和数据起始行。"
        style={{ marginBottom: 12 }}
      />
    );
  }
  const rowText = [
    meta.template_match?.templateName ? `模板：${meta.template_match.templateName} / v${meta.template_match.templateVersion}` : null,
    meta.template_match?.confidence ? `模板匹配把握：${readableConfidence(meta.template_match.confidence)}（得分 ${meta.template_match.matchScore}）` : null,
    meta.import_profile?.mappingSource ? `字段对应方式：${readableSystemValue(meta.import_profile.mappingSource)}` : null,
    meta.selected_sheet_name ? `工作表：${meta.selected_sheet_name}` : null,
    meta.selected_sheet_reason ? `工作表选择方式：${readableSystemValue(meta.selected_sheet_reason)}` : null,
    Number.isInteger(meta.header_row_index) ? `字段行：${Number(meta.header_row_index) + 1}` : null,
    meta.header_detection_confidence ? `字段行识别把握：${readableConfidence(meta.header_detection_confidence)}` : null,
    Number.isInteger(meta.header_detection_rows_scanned) ? `为识别字段名检查了前 ${meta.header_detection_rows_scanned} 行` : null,
    Number.isInteger(meta.unit_row_index) ? `单位行：${Number(meta.unit_row_index) + 1}` : null,
    Number.isInteger(meta.data_start_row_index) ? `数据起始行：${Number(meta.data_start_row_index) + 1}` : null,
    Array.isArray(meta.sheet_discovery) ? `检查了 ${meta.sheet_discovery.length} 个工作表` : null,
    Array.isArray(meta.merged_cells) && meta.merged_cells.length ? `合并区域：${meta.merged_cells.length}` : null,
  ].filter(Boolean).join('，');
  return (
    <Alert
      type="info"
      showIcon
      message={meta.layout_mode === 'manual' ? '已按手动设置读取文件' : '已自动识别表格结构'}
      description={rowText || '没有文件读取位置记录'}
      style={{ marginBottom: 12 }}
    />
  );
}

function PreviewTable({ rows, columns, dataset = false }: { rows: Record<string, unknown>[]; columns: DataHubColumn[]; dataset?: boolean }) {
  if (!rows.length) return <div className="datahub-empty">暂无预览数据</div>;
  const tableColumns = (columns.length ? columns : Object.keys(rows[0]).map((key) => ({ source_key: key, display_name: key } as DataHubColumn)))
    .slice(0, 18)
    .map((column) => {
      const dataIndex = dataset ? column.dataset_field || column.source_key : column.source_key;
      const title = dataset ? (column.dataset_field || column.display_name) : column.display_name;
      return {
        title: <span>{title}<br /><small style={{ color: 'var(--text-muted)' }}>{column.target_unit || column.unit || ''}</small></span>,
        dataIndex,
        key: dataIndex,
        width: 120,
        ellipsis: true,
        render: compactNumber,
      };
    });
  return (
    <Table
      size="small"
      rowKey="__preview_row_key"
      dataSource={rows.map((row, index) => ({ ...row, __preview_row_key: index }))}
      columns={tableColumns}
      pagination={false}
      scroll={{ x: Math.max(800, tableColumns.length * 120), y: 360 }}
    />
  );
}
